import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { EnvironmentBadge } from '@/components/EnvironmentBadge';
import { CleanupModeBanner } from '@/components/CleanupModeBanner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar, Area, AreaChart, ComposedChart } from 'recharts';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { Pie as ChartJSPie } from 'react-chartjs-2';

// Utilities
import { API, BACKEND_URL } from '@/utils/constants';
import { formatDate, todayLocalISO, dateToLocalISO } from '@/utils/date';
import { openPrintPreview } from '@/utils/print';

// Common Components
import { SignatureCanvas } from '@/components/common/SignatureCanvas';

// Pages
import { LoginPage } from '@/pages/auth';
import { PublicSignaturePage, QuoteApprovalPage } from '@/pages/public';
import { RemindersPage } from '@/pages/reminders';
import { AllJobsPage, JobDetail, NewJobForm } from '@/pages/jobs';
import { Dashboard } from '@/pages/dashboard';
import { CustomersPage } from '@/pages/customers';
import { SettingsPage } from '@/pages/settings';
import { ReportsPage } from '@/pages/reports';
import { NewsletterPage } from '@/pages/newsletter';
import { GlobalSearch } from '@/components/GlobalSearch';
import KioskPage from '@/pages/kiosk/KioskPage';
import { HelpPage, ChangelogPage } from '@/pages/help';
import { DateRangePicker, getDateBoundaries } from '@/components/common/DateRangePicker';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

// Import shared Auth Context
import { AuthContext, useAuth } from '@/context/AuthContext';

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoLogoutWarning, setAutoLogoutWarning] = useState(null);
  const [lastGlobalLogout, setLastGlobalLogout] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to check if a nav link is active
  const isNavActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Auto-logout timer effect
  useEffect(() => {
    if (!user) return;

    let intervalId;
    let warningTimeoutId;
    
    const checkAutoLogout = async () => {
      try {
        const response = await axios.get(`${API}/auth/check-logout`);
        const { auto_logout_enabled, auto_logout_time, auto_logout_warning_minutes, last_global_logout } = response.data;
        
        // Check for global logout
        if (last_global_logout) {
          const storedLogout = localStorage.getItem('lastGlobalLogout');
          if (storedLogout !== last_global_logout) {
            localStorage.setItem('lastGlobalLogout', last_global_logout);
            if (storedLogout) {
              // A new global logout occurred, force logout
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setUser(null);
              toast.info('You have been logged out from all devices');
              return;
            }
          }
        }
        
        if (!auto_logout_enabled) {
          setAutoLogoutWarning(null);
          return;
        }
        
        const now = new Date();
        const [hours, minutes] = auto_logout_time.split(':').map(Number);
        const logoutTime = new Date();
        logoutTime.setHours(hours, minutes, 0, 0);
        
        // If logout time has passed today, it's for tomorrow
        if (now > logoutTime) {
          setAutoLogoutWarning(null);
          return;
        }
        
        const msUntilLogout = logoutTime - now;
        const minutesUntilLogout = Math.floor(msUntilLogout / (1000 * 60));
        
        // Show warning if within warning period
        if (minutesUntilLogout <= auto_logout_warning_minutes && minutesUntilLogout > 0) {
          setAutoLogoutWarning({
            minutes: minutesUntilLogout,
            time: auto_logout_time
          });
        } else if (minutesUntilLogout <= 0) {
          // Time to logout
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          toast.info('You have been automatically logged out');
        } else {
          setAutoLogoutWarning(null);
        }
      } catch (error) {
        console.error('Auto-logout check failed:', error);
      }
    };
    
    // Check immediately and then every 30 seconds
    checkAutoLogout();
    intervalId = setInterval(checkAutoLogout, 30000);
    
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (warningTimeoutId) clearTimeout(warningTimeoutId);
    };
  }, [user]);

  const login = async (username, password) => {
    // Send any previously-issued device token for this username so trusted browsers skip 2FA
    const deviceToken = localStorage.getItem(`device_token_${username}`) || null;
    const response = await axios.post(`${API}/auth/login`, { username, password, device_token: deviceToken });
    const data = response.data;
    if (data.needs_2fa) {
      // Caller (LoginPage) should switch to the 2FA prompt step
      return {
        ok: false,
        needs_2fa: true,
        session_id: data.session_id,
        methods: data.methods || [],
        masked_email: data.masked_email,
        masked_phone: data.masked_phone,
        username,
      };
    }
    const { access_token, user: userData } = data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Feb 2026 — clear any stale `lastGlobalLogout` inherited from a
    // previous session. Otherwise the first checkAutoLogout() after login
    // sees stored !== server and force-logs out the freshly-logged-in
    // user. On the next attempt the value is in sync and login persists,
    // which matches the "first attempt logs out, second works" symptom.
    localStorage.removeItem('lastGlobalLogout');
    setUser(userData);
    toast.success(`Welcome, ${userData.full_name}!`);
    return { ok: true, user: userData };
  };

  const verify2FA = async ({ session_id, code, remember_device, method = 'totp', username }) => {
    const endpoint = method === 'recovery'
      ? `${API}/auth/2fa/recovery`
      : (method === 'email' || method === 'sms')
        ? `${API}/auth/2fa/verify-otp`
        : `${API}/auth/2fa/verify`;
    const body = method === 'recovery'
      ? { session_id, recovery_code: code }
      : { session_id, code, remember_device: !!remember_device };
    const { data } = await axios.post(endpoint, body);
    const { access_token, user: userData, device_token } = data;
    if (device_token && username) {
      localStorage.setItem(`device_token_${username}`, device_token);
    }
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Same reasoning as in login() above — clear the stale marker so the
    // first post-2FA checkAutoLogout doesn't spuriously trigger.
    localStorage.removeItem('lastGlobalLogout');
    setUser(userData);
    toast.success(`Welcome, ${userData.full_name}!`);
    return { ok: true, user: userData, codes_remaining: data.codes_remaining };
  };

  const send2FAOtp = async ({ session_id, channel }) => {
    const endpoint = channel === 'sms' ? `${API}/auth/2fa/send-sms` : `${API}/auth/2fa/send-email`;
    const { data } = await axios.post(endpoint, { session_id });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logged out successfully');
  };

  // Configure axios to include token in all requests
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Handle 401 errors globally
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        toast.error('Session expired. Please login again.');
      }
      return Promise.reject(error);
    }
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // ===== PURCHASES PAGE COMPONENT =====
  const PurchasesPage = () => {
    const [purchases, setPurchases] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [stockItems, setStockItems] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showViewDialog, setShowViewDialog] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showAddStockDialog, setShowAddStockDialog] = useState(false);
    const [showAddSupplierDialog, setShowAddSupplierDialog] = useState(false);
    const [currentLineItemIndex, setCurrentLineItemIndex] = useState(null);
    const [editingPurchaseId, setEditingPurchaseId] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [purchaseToDelete, setPurchaseToDelete] = useState(null);

    // Supplier typeahead state
    const [supplierSearch, setSupplierSearch] = useState('');
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const supplierInputRef = useRef(null);
    
    // Internal / Business segmented filter (same UX as the Expenses page)
    const [internalFilter, setInternalFilter] = useState('all'); // 'all' | 'business' | 'internal'
    // Payment-method click-to-filter (from the Payment Methods Breakdown card).
    // Narrows the transactions table for month-end statement reconciliation.
    const [pmFilter, setPmFilter] = useState(null);
    // Reconciliation filter — hides already-ticked rows.
    const [unreconciledOnly, setUnreconciledOnly] = useState(false);

    // Combined client-side filter that stacks on top of server-filtered data.
    const visiblePurchases = purchases
      .filter(p => !pmFilter || (p.payment_method || 'Unspecified') === pmFilter)
      .filter(p => !unreconciledOnly || !p.reconciled);

    // Toggle reconciled flag on a single purchase. Optimistic UI.
    const toggleReconciled = async (purchaseId, currentValue) => {
      const nextValue = !currentValue;
      setPurchases(prev => prev.map(p => p.id === purchaseId
        ? { ...p, reconciled: nextValue, reconciled_at: nextValue ? new Date().toISOString() : null }
        : p));
      try {
        await axios.patch(`${API}/purchases/${purchaseId}/reconciled`, { reconciled: nextValue });
      } catch (err) {
        setPurchases(prev => prev.map(p => p.id === purchaseId
          ? { ...p, reconciled: currentValue }
          : p));
        toast.error(err?.response?.data?.detail || 'Failed to update reconciled status');
      }
    };

    const filteredSuppliers = useMemo(() => {
      if (!supplierSearch) return suppliers;
      const s = supplierSearch.toLowerCase();
      return suppliers.filter(sup => sup.name.toLowerCase().includes(s));
    }, [suppliers, supplierSearch]);

    // Stock-item typeahead component (per line item, owns its own search state)
    const StockItemTypeahead = ({ value, itemName, onSelect, onAddNew, items }) => {
      const [search, setSearch] = useState(itemName || '');
      const [showDropdown, setShowDropdown] = useState(false);
      const inputRef = useRef(null);

      // Sync search if parent changes itemName (e.g., after creating new stock)
      useEffect(() => {
        if (itemName !== undefined && itemName !== search) {
          setSearch(itemName);
        }
      }, [itemName]);

      const filtered = useMemo(() => {
        if (!search) return items;
        const s = search.toLowerCase();
        return items.filter(it => {
          const label = `${it.brand ? it.brand + ' ' : ''}${it.item_name || ''} ${it.item_code || ''} ${it.sku || ''}`.toLowerCase();
          return label.includes(s);
        });
      }, [items, search]);

      const handleSelect = (it) => {
        onSelect(it);
        setSearch(it.item_name);
        setShowDropdown(false);
      };

      const handleClear = () => {
        onSelect(null);
        setSearch('');
        if (inputRef.current) inputRef.current.focus();
      };

      return (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Input
              ref={inputRef}
              type="text"
              value={search}
              placeholder="Type to search items..."
              autoComplete="off"
              onChange={(e) => {
                setSearch(e.target.value);
                setShowDropdown(true);
                if (value && e.target.value !== itemName) onSelect(null);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              style={{ fontSize: '0.875rem', paddingRight: search ? '28px' : undefined }}
              required={!value}
              data-testid="purchase-stock-item-input"
            />
            {search && (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280',
                  fontSize: '0.875rem', padding: '0 4px'
                }}
                aria-label="Clear stock item"
                tabIndex={-1}
              >
                ✕
              </button>
            )}
            {showDropdown && filtered.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '6px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                maxHeight: '220px', overflowY: 'auto', zIndex: 60, marginTop: '4px'
              }}>
                {filtered.slice(0, 50).map(it => (
                  <div
                    key={it.id}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(it); }}
                    style={{
                      padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                      fontSize: '0.825rem',
                      backgroundColor: value === it.id ? '#eff6ff' : 'transparent'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = value === it.id ? '#eff6ff' : 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>
                      {it.brand ? `${it.brand} - ` : ''}{it.item_name}
                    </div>
                    {(it.item_code || it.sku) && (
                      <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        {it.item_code && `Code: ${it.item_code}`}
                        {it.item_code && it.sku && ' | '}
                        {it.sku && `SKU: ${it.sku}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddNew}
            style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            title="Add new stock item"
          >
            + New
          </Button>
        </div>
      );
    };

    const [filters, setFilters] = useState({
      supplier_id: '',
      date_from: '',
      date_to: ''
    });
    // New uniform date range picker state (matches Reports / Customers pages)
    const [dateRange, setDateRange] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    // Filter-card supplier typeahead state
    const [filterSupplierSearch, setFilterSupplierSearch] = useState('');
    const [showFilterSupplierDropdown, setShowFilterSupplierDropdown] = useState(false);
    const filterSupplierRef = useRef(null);
    const [formData, setFormData] = useState({
      supplier_id: '',
      purchase_date: todayLocalISO(),
      invoice_number: '',
      notes: '',
      gst_percentage: 10.0,
      is_internal: false,
      personal_use_claimed: false,
      status: 'Finished',
      payment_method: '',
      line_items: []
    });
    const [newStockFormData, setNewStockFormData] = useState({
      brand: '',
      item_name: '',
      item_code: '',
      sku: '',
      description: '',
      category: '',
      unit_of_measure: '',
      cost_price: 0,
      sell_price: 0,
      current_quantity: 0,
      low_stock_threshold: 0,
      supplier_id: ''
    });
    const [newSupplierFormData, setNewSupplierFormData] = useState({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      abn: '',
      payment_terms: ''
    });

    useEffect(() => {
      fetchData();
    }, []);

    const fetchData = async () => {
      try {
        const [purchasesResponse, suppliersResponse, stockResponse, settingsResponse] = await Promise.all([
          axios.get(`${API}/purchases`),
          axios.get(`${API}/suppliers`),
          axios.get(`${API}/stock-items`),
          axios.get(`${API}/settings`)
        ]);
        setPurchases(purchasesResponse.data);
        setSuppliers(suppliersResponse.data);
        setStockItems(stockResponse.data);
        setSettings(settingsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load purchases');
      } finally {
        setLoading(false);
      }
    };

    const fetchPurchases = async () => {
      try {
        const params = new URLSearchParams();
        if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
        // Drive date params from the uniform preset
        const { start, end } = getDateBoundaries(dateRange, customStartDate, customEndDate);
        if (start) params.append('date_from', start);
        if (end) params.append('date_to', end);
        if (internalFilter && internalFilter !== 'all') params.append('internal_filter', internalFilter);
        
        const response = await axios.get(`${API}/purchases?${params.toString()}`);
        setPurchases(response.data);
      } catch (error) {
        console.error('Error fetching purchases:', error);
        toast.error('Failed to load purchases');
      }
    };

    // Auto-refetch when global-style date range, supplier, or internal filter changes
    useEffect(() => {
      fetchPurchases();
      // eslint-disable-next-line
    }, [dateRange, customStartDate, customEndDate, filters.supplier_id, internalFilter]);

    const handleFilterChange = (field, value) => {
      setFilters({ ...filters, [field]: value });
    };

    const handleApplyFilters = () => {
      fetchPurchases();
    };

    const handleClearFilters = () => {
      setFilters({
        supplier_id: '',
        date_from: '',
        date_to: ''
      });
      setDateRange('all');
      setCustomStartDate('');
      setCustomEndDate('');
      setInternalFilter('all');
    };

    // Calculate totals for summary cards
    const getTotalPurchases = () => purchases.reduce((sum, p) => sum + (p.total_cost || 0), 0);
    const getTotalGST = () => purchases.reduce((sum, p) => sum + (p.gst_amount || 0), 0);
    const getTotalSubtotal = () => purchases.reduce((sum, p) => sum + (p.subtotal || 0), 0);

    // Group the currently-filtered purchases by payment_method. Powers the
    // "Payment Methods Breakdown" card — shows how much you spent per method
    // for the active date range so you can quickly see "$X on Amex, $Y on Card".
    const paymentMethodBreakdown = useMemo(() => {
      const groups = new Map();
      for (const p of purchases) {
        const key = (p.payment_method || 'Unspecified').trim() || 'Unspecified';
        const g = groups.get(key) || { method: key, count: 0, subtotal: 0, total: 0 };
        g.count += 1;
        g.subtotal += Number(p.subtotal || 0);
        g.total += Number(p.total_cost || 0);
        groups.set(key, g);
      }
      const rows = [...groups.values()].sort((a, b) => b.total - a.total);
      const grandTotal = rows.reduce((s, r) => s + r.total, 0);
      return rows.map(r => ({ ...r, pct: grandTotal > 0 ? (r.total / grandTotal) * 100 : 0 }));
    }, [purchases]);

    const handleAdd = () => {
      setFormData({
        supplier_id: '',
        purchase_date: todayLocalISO(),
        invoice_number: '',
        notes: '',
        gst_percentage: 10.0,
        is_internal: false,
        personal_use_claimed: false,
        status: 'Finished',
        payment_method: settings?.default_payment_method || 'Bank Transfer',
        line_items: []
      });
      setShowAddDialog(true);
    };

    const handleAddLineItem = () => {
      setFormData({
        ...formData,
        line_items: [
          ...formData.line_items,
          {
            stock_item_id: '',
            stock_item_name: '',
            quantity: 1,
            cost_per_unit: 0,
            total_cost: 0
          }
        ]
      });
    };

    const handleRemoveLineItem = (index) => {
      const newLineItems = formData.line_items.filter((_, i) => i !== index);
      setFormData({ ...formData, line_items: newLineItems });
    };

    const handleLineItemChange = (index, field, value) => {
      const newLineItems = [...formData.line_items];
      newLineItems[index][field] = value;

      // If stock item changed, update the name
      if (field === 'stock_item_id') {
        const selectedItem = stockItems.find(item => item.id === value);
        if (selectedItem) {
          newLineItems[index].stock_item_name = selectedItem.item_name;
        }
      }

      // Calculate total_cost
      if (field === 'quantity' || field === 'cost_per_unit') {
        const quantity = parseFloat(newLineItems[index].quantity) || 0;
        const costPerUnit = parseFloat(newLineItems[index].cost_per_unit) || 0;
        newLineItems[index].total_cost = quantity * costPerUnit;
      }

      setFormData({ ...formData, line_items: newLineItems });
    };

    const handleSubmitAdd = async (e) => {
      e.preventDefault();

      if (formData.line_items.length === 0) {
        toast.error('Please add at least one item to the purchase');
        return;
      }

      try {
        if (editingPurchaseId) {
          await axios.put(`${API}/purchases/${editingPurchaseId}`, formData);
          toast.success('Purchase updated successfully! Stock quantities adjusted.');
        } else {
          await axios.post(`${API}/purchases`, formData);
          toast.success('Purchase recorded successfully! Stock quantities updated.');
        }
        setShowAddDialog(false);
        setEditingPurchaseId(null);
        fetchData();
      } catch (error) {
        console.error('Error saving purchase:', error);
        toast.error(editingPurchaseId ? 'Failed to update purchase' : 'Failed to record purchase');
      }
    };

    const handleEditPurchase = (purchase) => {
      setEditingPurchaseId(purchase.id);
      setShowViewDialog(false);
      setFormData({
        supplier_id: purchase.supplier_id || '',
        purchase_date: typeof purchase.purchase_date === 'string'
          ? purchase.purchase_date.split('T')[0]
          : dateToLocalISO(new Date(purchase.purchase_date)),
        invoice_number: purchase.invoice_number || '',
        notes: purchase.notes || '',
        gst_percentage: purchase.gst_percentage ?? 10.0,
        is_internal: !!purchase.is_internal,
        personal_use_claimed: !!purchase.personal_use_claimed,
        status: purchase.status || 'Finished',
        payment_method: purchase.payment_method || 'Bank Transfer',
        line_items: (purchase.line_items || []).map(li => ({
          stock_item_id: li.stock_item_id || '',
          stock_item_name: li.stock_item_name || '',
          quantity: li.quantity || 0,
          cost_per_unit: li.cost_per_unit || 0,
          total_cost: li.total_cost || 0
        }))
      });
      setSupplierSearch(purchase.supplier_name || '');
      setShowAddDialog(true);
    };

    // Feb 2026 — Copy a purchase as a starting point for a new one. Carries
    // supplier / GST% / payment method / personal flags / notes / status, and
    // the *line items* (stock refs + qty + cost, so re-ordering the same
    // drives is one click) but blanks purchase_date → today and invoice
    // number, so the user only enters what actually differs each time.
    const handleCopyPurchase = (purchase) => {
      setEditingPurchaseId(null); // treat as a brand-new purchase, not an edit
      setShowViewDialog(false);
      setFormData({
        supplier_id: purchase.supplier_id || '',
        purchase_date: todayLocalISO(),                 // today by default
        invoice_number: '',                             // usually differs
        notes: purchase.notes || '',
        gst_percentage: purchase.gst_percentage ?? 10.0,
        is_internal: !!purchase.is_internal,
        personal_use_claimed: !!purchase.personal_use_claimed,
        status: purchase.status || 'Finished',
        payment_method: purchase.payment_method || 'Bank Transfer',
        line_items: (purchase.line_items || []).map(li => ({
          stock_item_id: li.stock_item_id || '',
          stock_item_name: li.stock_item_name || '',
          quantity: li.quantity || 0,
          cost_per_unit: li.cost_per_unit || 0,
          total_cost: li.total_cost || 0
        }))
      });
      setSupplierSearch(purchase.supplier_name || '');
      setShowAddDialog(true);
      toast.info(`Copying from ${purchase.supplier_name || 'previous'} purchase — please review invoice #, date and quantities`);
    };

    const handleDeletePurchase = async () => {
      if (!purchaseToDelete) return;
      try {
        await axios.delete(`${API}/purchases/${purchaseToDelete.id}`);
        toast.success('Purchase deleted and stock reverted');
        setShowDeleteDialog(false);
        setPurchaseToDelete(null);
        setShowViewDialog(false);
        fetchData();
      } catch (error) {
        console.error('Error deleting purchase:', error);
        toast.error('Failed to delete purchase');
      }
    };

    const getTotalCost = () => {
      return formData.line_items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    };

    const calculateGSTBreakdown = () => {
      const total = getTotalCost();
      const gstMultiplier = 1 + (formData.gst_percentage / 100);
      const subtotal = total / gstMultiplier;
      const gstAmount = total - subtotal;
      return { total, subtotal, gstAmount };
    };

    const handleOpenAddStockDialog = (lineItemIndex) => {
      setCurrentLineItemIndex(lineItemIndex);
      // Pre-fill supplier if one is selected in the purchase form
      setNewStockFormData({
        brand: '',
        item_name: '',
        item_code: '',
        sku: '',
        description: '',
        category: '',
        unit_of_measure: 'Each',
        cost_price: 0,
        sell_price: 0,
        current_quantity: 0,
        low_stock_threshold: 5,
        supplier_id: formData.supplier_id || ''
      });
      setShowAddStockDialog(true);
    };

    const handleSubmitNewStock = async (e) => {
      e.preventDefault();
      
      try {
        const response = await axios.post(`${API}/stock-items`, newStockFormData);
        const newStockItem = response.data;
        
        // Add the new stock item to the list
        setStockItems(prev => [...prev, newStockItem]);
        
        // Auto-select the new stock item in the current line item
        if (currentLineItemIndex !== null) {
          const newLineItems = [...formData.line_items];
          newLineItems[currentLineItemIndex].stock_item_id = newStockItem.id;
          newLineItems[currentLineItemIndex].stock_item_name = newStockItem.item_name;
          setFormData({ ...formData, line_items: newLineItems });
        }
        
        toast.success(`Stock item "${newStockItem.item_name}" created successfully!`);
        setShowAddStockDialog(false);
        setCurrentLineItemIndex(null);
      } catch (error) {
        console.error('Error creating stock item:', error);
        toast.error('Failed to create stock item');
      }
    };

    const handleOpenAddSupplierDialog = (prefillName = '') => {
      setNewSupplierFormData({
        name: prefillName,
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        abn: '',
        payment_terms: 'Net 30'
      });
      setShowAddSupplierDialog(true);
    };

    const handleSubmitNewSupplier = async (e) => {
      e.preventDefault();
      
      try {
        const response = await axios.post(`${API}/suppliers`, newSupplierFormData);
        const newSupplier = response.data;
        
        // Add the new supplier to the list
        setSuppliers(prev => [...prev, newSupplier]);
        
        // Auto-select the new supplier in the purchase form
        setFormData({ ...formData, supplier_id: newSupplier.id });
        setSupplierSearch(newSupplier.name);
        
        toast.success(`Supplier "${newSupplier.name}" created successfully!`);
        setShowAddSupplierDialog(false);
      } catch (error) {
        console.error('Error creating supplier:', error);
        toast.error('Failed to create supplier');
      }
    };

    // Keep supplier search input in sync with selected supplier_id
    useEffect(() => {
      if (formData.supplier_id) {
        const sup = suppliers.find(s => s.id === formData.supplier_id);
        if (sup && sup.name !== supplierSearch) {
          setSupplierSearch(sup.name);
        }
      } else if (!formData.supplier_id && supplierSearch && !showSupplierDropdown) {
        // form was reset
        setSupplierSearch('');
      }
    }, [formData.supplier_id, suppliers]);

    if (loading) {
      return <div className="loading">Loading purchases...</div>;
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD'
      }).format(amount);
    };

    return (
      <div className="purchases-page">
        <div className="page-header">
          <h2>Purchase Records</h2>
          <Button onClick={handleAdd} data-testid="record-purchase-btn">
            + Record Purchase
          </Button>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Purchases</CardDescription>
              <CardTitle className="text-2xl" style={{ color: '#dc2626' }}>
                {formatCurrency(getTotalPurchases())}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Subtotal (ex GST)</CardDescription>
              <CardTitle className="text-2xl">
                {formatCurrency(getTotalSubtotal())}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>GST Paid</CardDescription>
              <CardTitle className="text-2xl">
                {formatCurrency(getTotalGST())}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Purchase Count</CardDescription>
              <CardTitle className="text-2xl">{purchases.length}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Quick Entry Section — one-tap chips to pre-fill Record Purchase.
            Manage these at Settings → Dropdown Values → Purchase Quick Entry Templates. */}
        {settings?.purchase_quick_templates && settings.purchase_quick_templates.length > 0 && (
          <Card style={{ marginBottom: '1.5rem', backgroundColor: '#f8fafc' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">⚡ Quick Entry</CardTitle>
              <CardDescription>Click a template to pre-fill a common purchase</CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {settings.purchase_quick_templates.map((template, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Try to resolve supplier_id from the supplier_name in the template
                      const matchedSupplier = template.supplier_name
                        ? suppliers.find(s => (s.name || '').toLowerCase() === template.supplier_name.toLowerCase())
                        : null;
                      setFormData({
                        supplier_id: matchedSupplier?.id || '',
                        purchase_date: todayLocalISO(),
                        invoice_number: '',
                        notes: template.notes || '',
                        gst_percentage: 10.0,
                        is_internal: false,
                        personal_use_claimed: false,
                        status: 'Finished',
                        payment_method: template.payment_method || settings?.default_payment_method || 'Bank Transfer',
                        line_items: []
                      });
                      // Pre-populate the supplier typeahead if we matched
                      if (matchedSupplier) setSupplierSearch(matchedSupplier.name);
                      setShowAddDialog(true);
                    }}
                    data-testid={`purchase-quick-entry-${idx}`}
                  >
                    {template.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Methods Breakdown — how you paid, for the current date range.
            Purely a read view over the already-filtered `purchases` array.
            Click a row to narrow the table below to just that method — perfect
            for reconciling against a bank / credit-card / PayPal statement. */}
        {paymentMethodBreakdown.length > 0 && (
          <Card style={{ marginBottom: '1.5rem' }} data-testid="purchase-payment-method-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">💳 Payment Methods Breakdown</CardTitle>
              <CardDescription>
                How you paid for these purchases (respects the date filter below).
                Click a row to filter the purchases table by that method.
                {pmFilter && (
                  <span
                    onClick={(e) => { e.stopPropagation(); setPmFilter(null); }}
                    style={{
                      marginLeft: '0.5rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.1rem 0.5rem',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      fontWeight: 600,
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                    }}
                    data-testid="purchase-pm-filter-chip"
                  >
                    Filtered: {pmFilter} ✕
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Method</TableHead>
                    <TableHead style={{ textAlign: 'right' }}># Purchases</TableHead>
                    <TableHead style={{ textAlign: 'right' }}>Subtotal (ex GST)</TableHead>
                    <TableHead style={{ textAlign: 'right' }}>Total (inc GST)</TableHead>
                    <TableHead style={{ textAlign: 'right', width: '80px' }}>% of total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentMethodBreakdown.map((row) => {
                    const isActive = pmFilter === row.method;
                    return (
                      <TableRow
                        key={row.method}
                        data-testid={`purchase-pm-row-${row.method.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => setPmFilter(isActive ? null : row.method)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isActive ? '#dbeafe' : undefined,
                          fontWeight: isActive ? 600 : undefined,
                        }}
                        className="hover:bg-slate-50"
                      >
                        <TableCell style={{ fontWeight: 500 }}>{row.method}</TableCell>
                        <TableCell style={{ textAlign: 'right' }}>{row.count}</TableCell>
                        <TableCell style={{ textAlign: 'right' }}>{formatCurrency(row.subtotal)}</TableCell>
                        <TableCell style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(row.total)}</TableCell>
                        <TableCell style={{ textAlign: 'right', color: '#64748b' }}>{row.pct.toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>
                    <TableCell>Total</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{paymentMethodBreakdown.reduce((s, r) => s + r.count, 0)}</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{formatCurrency(paymentMethodBreakdown.reduce((s, r) => s + r.subtotal, 0))}</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{formatCurrency(paymentMethodBreakdown.reduce((s, r) => s + r.total, 0))}</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader>
            <CardTitle className="text-lg">Filter Purchases</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
              <DateRangePicker
                dateRange={dateRange}
                setDateRange={setDateRange}
                startDate={customStartDate}
                setStartDate={setCustomStartDate}
                endDate={customEndDate}
                setEndDate={setCustomEndDate}
                label="Date Range"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '2fr auto', gap: '1rem', alignItems: 'end' }}>
                <div ref={filterSupplierRef} style={{ position: 'relative' }}>
                  <Label>Supplier</Label>
                  <Input
                    type="text"
                    placeholder="Start typing a supplier name..."
                    value={filterSupplierSearch}
                    onChange={(e) => {
                      setFilterSupplierSearch(e.target.value);
                      setShowFilterSupplierDropdown(true);
                      if (!e.target.value) handleFilterChange('supplier_id', '');
                    }}
                    onFocus={() => setShowFilterSupplierDropdown(true)}
                    onBlur={() => setTimeout(() => setShowFilterSupplierDropdown(false), 200)}
                    data-testid="purchase-filter-supplier-input"
                  />
                  {showFilterSupplierDropdown && (() => {
                    const q = filterSupplierSearch.toLowerCase().trim();
                    const list = [...suppliers]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .filter(s => !q || (s.name || '').toLowerCase().includes(q));
                    if (list.length === 0) return null;
                    return (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '260px', overflowY: 'auto', background: 'white', border: '1px solid #cbd5e1', borderRadius: '0.375rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, marginTop: '0.25rem' }}>
                        <div
                          onClick={() => { handleFilterChange('supplier_id', ''); setFilterSupplierSearch(''); setShowFilterSupplierDropdown(false); }}
                          style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #e2e8f0', fontStyle: 'italic', color: '#475569' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          All suppliers
                        </div>
                        {list.slice(0, 100).map((s) => (
                          <div
                            key={s.id}
                            onClick={() => { handleFilterChange('supplier_id', s.id); setFilterSupplierSearch(s.name); setShowFilterSupplierDropdown(false); }}
                            onMouseDown={(e) => e.preventDefault()}
                            style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', backgroundColor: filters.supplier_id === s.id ? '#eff6ff' : 'transparent' }}
                            data-testid={`purchase-filter-supplier-option-${s.id}`}
                          >
                            {s.name}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <Button variant="outline" onClick={() => { handleClearFilters(); setFilterSupplierSearch(''); }} data-testid="clear-filters-btn">Clear All</Button>
                </div>
              </div>

              {/* Internal vs Business segmented filter — same UX as Expenses page */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                <Label style={{ marginBottom: 0 }}>Show:</Label>
                {[
                  { value: 'all', label: 'All Purchases' },
                  { value: 'business', label: 'Business Only' },
                  { value: 'internal', label: 'Internal / Personal Only' },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={internalFilter === opt.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setInternalFilter(opt.value)}
                    data-testid={`purchase-internal-filter-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Button
                    type="button"
                    variant={unreconciledOnly ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUnreconciledOnly(!unreconciledOnly)}
                    data-testid="purchase-unreconciled-filter"
                  >
                    {unreconciledOnly ? '☑ Showing unreconciled only' : '☐ Show unreconciled only'}
                  </Button>
                  {purchases.length > 0 && (() => {
                    const reconciled = purchases.filter(p => p.reconciled).length;
                    const total = purchases.length;
                    return (
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {reconciled} / {total} reconciled ({total > 0 ? Math.round(reconciled * 100 / total) : 0}%)
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Purchases Table */}
        <Card>
          <CardHeader>
            <CardTitle>Purchase History ({visiblePurchases.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: '60px', textAlign: 'center' }} title="Tick when reconciled against your statement">☑</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Subtotal</TableHead>
                  <TableHead>GST</TableHead>
                  <TableHead>Total Cost</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiblePurchases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>
                      {unreconciledOnly && purchases.length > 0
                        ? `All ${purchases.length} matching purchase(s) reconciled ✓ — untick "Show unreconciled only" to see them again.`
                        : pmFilter
                          ? `No purchases paid via ${pmFilter} in the current filters. Click the highlighted row in Payment Methods Breakdown to clear.`
                          : (`No purchases found. ${filters.supplier_id || filters.date_from || filters.date_to ? 'Try adjusting your filters.' : 'Click "Record Purchase" to add your first purchase.'}`)}
                    </TableCell>
                  </TableRow>
                ) : (
                  visiblePurchases.map((purchase) => (
                    <TableRow
                      key={purchase.id}
                      data-testid={`purchase-row-${purchase.id}`}
                      style={purchase.reconciled ? { backgroundColor: '#f0fdf4', opacity: 0.85 } : undefined}
                    >
                      <TableCell style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!purchase.reconciled}
                          onChange={() => toggleReconciled(purchase.id, purchase.reconciled)}
                          data-testid={`purchase-reconciled-${purchase.id}`}
                          title={purchase.reconciled_at ? `Reconciled ${new Date(purchase.reconciled_at).toLocaleDateString()} by ${purchase.reconciled_by || 'unknown'}` : 'Tick when matched against your statement'}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#059669' }}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(purchase.purchase_date)}
                      </TableCell>
                      <TableCell style={{ fontWeight: '600' }}>{purchase.supplier_name}</TableCell>
                      <TableCell>{purchase.invoice_number || '-'}</TableCell>
                      <TableCell>{purchase.line_items.length} item(s)</TableCell>
                      <TableCell>${purchase.subtotal?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell>
                        ${purchase.gst_amount?.toFixed(2) || '0.00'} ({purchase.gst_percentage || 10}%)
                      </TableCell>
                      <TableCell style={{ fontWeight: '600' }}>
                        ${purchase.total_cost.toFixed(2)}
                      </TableCell>
                      <TableCell style={{ color: '#475569', fontSize: '0.85rem' }} data-testid={`purchase-payment-method-${purchase.id}`}>
                        {purchase.payment_method || '-'}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const isBooked = (purchase.status || 'Finished') === 'Booked';
                          return (
                            <span
                              data-testid={`purchase-status-badge-${purchase.id}`}
                              style={{
                                display: 'inline-block',
                                padding: '0.15rem 0.55rem',
                                borderRadius: '999px',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                                color: isBooked ? '#92400e' : '#065f46',
                                backgroundColor: isBooked ? '#fef3c7' : '#d1fae5',
                                border: `1px solid ${isBooked ? '#fcd34d' : '#6ee7b7'}`
                              }}
                              title={isBooked ? 'Provisional — money out, not yet realised on P&L' : 'Realised purchase — included in P&L'}
                            >
                              {isBooked ? 'BOOKED' : 'FINISHED'}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedPurchase(purchase);
                              setShowViewDialog(true);
                            }}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPurchase(purchase)}
                            data-testid={`edit-purchase-${purchase.id}`}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyPurchase(purchase)}
                            data-testid={`copy-purchase-${purchase.id}`}
                            title="Create a new purchase pre-filled from this one — date and invoice # will be blank"
                          >
                            📋 Copy
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            style={{ color: '#dc2626' }}
                            onClick={() => {
                              setPurchaseToDelete(purchase);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-purchase-${purchase.id}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add Purchase Dialog */}
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) setEditingPurchaseId(null);
        }}>
          <DialogContent style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <DialogHeader>
              <DialogTitle>{editingPurchaseId ? 'Edit Purchase' : 'Record New Purchase'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitAdd}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Supplier *
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <Input
                        ref={supplierInputRef}
                        type="text"
                        value={supplierSearch}
                        placeholder="Type to search suppliers..."
                        autoComplete="off"
                        required={!formData.supplier_id}
                        onChange={(e) => {
                          setSupplierSearch(e.target.value);
                          setShowSupplierDropdown(true);
                          if (formData.supplier_id) {
                            setFormData({ ...formData, supplier_id: '' });
                          }
                        }}
                        onFocus={() => setShowSupplierDropdown(true)}
                        onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                        style={{ paddingRight: supplierSearch ? '28px' : undefined }}
                        data-testid="purchase-supplier-input"
                      />
                      {supplierSearch && (
                        <button
                          type="button"
                          onClick={() => {
                            setSupplierSearch('');
                            setFormData({ ...formData, supplier_id: '' });
                            if (supplierInputRef.current) supplierInputRef.current.focus();
                          }}
                          style={{
                            position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280',
                            fontSize: '0.875rem', padding: '0 4px'
                          }}
                          aria-label="Clear supplier"
                          tabIndex={-1}
                        >
                          ✕
                        </button>
                      )}
                      {showSupplierDropdown && filteredSuppliers.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0,
                          backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '6px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          maxHeight: '220px', overflowY: 'auto', zIndex: 60, marginTop: '4px'
                        }}>
                          {filteredSuppliers.slice(0, 50).map(sup => (
                            <div
                              key={sup.id}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setFormData({ ...formData, supplier_id: sup.id });
                                setSupplierSearch(sup.name);
                                setShowSupplierDropdown(false);
                              }}
                              style={{
                                padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                                backgroundColor: formData.supplier_id === sup.id ? '#eff6ff' : 'transparent'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = formData.supplier_id === sup.id ? '#eff6ff' : 'transparent'}
                            >
                              <div style={{ fontWeight: '500' }}>{sup.name}</div>
                              {sup.contact_person && (
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{sup.contact_person}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {showSupplierDropdown && supplierSearch && filteredSuppliers.length === 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0,
                          backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '6px',
                          padding: '12px', zIndex: 60, marginTop: '4px',
                          fontSize: '0.875rem', color: '#6b7280',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem'
                        }}>
                          <span>No suppliers matching "<strong>{supplierSearch}</strong>".</span>
                          <button
                            type="button"
                            data-testid="purchase-supplier-inline-add"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setShowSupplierDropdown(false);
                              handleOpenAddSupplierDialog(supplierSearch);
                            }}
                            style={{
                              background: '#16a34a', color: '#fff', border: 'none',
                              padding: '6px 12px', borderRadius: '4px', fontSize: '0.8rem',
                              fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
                            }}
                            title={`Create a new supplier called "${supplierSearch}"`}
                          >
                            + Add {supplierSearch.length > 15 ? supplierSearch.slice(0, 15) + '…' : supplierSearch}
                          </button>
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleOpenAddSupplierDialog}
                      style={{ whiteSpace: 'nowrap' }}
                      title="Add new supplier"
                    >
                      + New
                    </Button>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Purchase Date *
                  </label>
                  <Input
                    type="date"
                    required
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Invoice Number
                  </label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    placeholder="e.g., INV-12345"
                  />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', fontWeight: '500' }}>
                    <span>GST %</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', cursor: 'pointer', color: '#475569', fontWeight: 'normal' }}>
                      <input
                        type="checkbox"
                        checked={(formData.gst_percentage || 0) === 0}
                        onChange={(e) => setFormData({ ...formData, gst_percentage: e.target.checked ? 0 : 10 })}
                        data-testid="purchase-gst-free-toggle"
                      />
                      GST-Free
                    </label>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.gst_percentage}
                    onChange={(e) => setFormData({ ...formData, gst_percentage: parseFloat(e.target.value) || 0 })}
                    placeholder="10.0"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Notes
                  </label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes"
                  />
                </div>
              </div>

              {/* Bucket selector (Feb 2026) — replaces the old is_internal
                  switch with a 3-way select so users can distinguish between
                  "personal, not claimed" (excluded from P&L) and "personal,
                  still claimed" (kept in P&L but tracked as personal-use for
                  the owner's own EOFY reconciliation). The bucket value maps
                  cleanly to the two mutually-exclusive backend flags. */}
              {(() => {
                const bucket = formData.is_internal
                  ? 'personal_not_claimed'
                  : (formData.personal_use_claimed ? 'personal_claimed' : 'business');
                const bg = bucket === 'personal_not_claimed' ? '#fef3c7'
                          : bucket === 'personal_claimed'    ? '#fef2f2'
                          : '#f8fafc';
                const bd = bucket === 'personal_not_claimed' ? '#fcd34d'
                          : bucket === 'personal_claimed'    ? '#fca5a5'
                          : '#e2e8f0';
                return (
              <div style={{ padding: '0.75rem 1rem', backgroundColor: bg, border: '1px solid', borderColor: bd, borderRadius: '0.5rem', marginBottom: '1rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem' }}>Category type</div>
                <select
                  value={bucket}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({
                      ...formData,
                      is_internal:           v === 'personal_not_claimed',
                      personal_use_claimed:  v === 'personal_claimed',
                    });
                  }}
                  data-testid="purchase-bucket-select"
                  style={{ padding: '0.4rem 0.6rem', borderRadius: '0.375rem', border: '1px solid #cbd5e1', fontWeight: 600, width: '100%', marginBottom: '0.35rem' }}
                >
                  <option value="business">Business (default) — normal deductible purchase</option>
                  <option value="personal_not_claimed">Personal — Not Claimed (excluded from P&amp;L)</option>
                  <option value="personal_claimed">Personal — Claimed (kept in P&amp;L, flagged as personal-use)</option>
                </select>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                  {bucket === 'business' && 'Fully deductible business purchase. Appears in your standard P&L.'}
                  {bucket === 'personal_not_claimed' && 'Paid from business account but not for business use. EXCLUDED from P&L — not deducted for tax.'}
                  {bucket === 'personal_claimed' && 'Kept in P&L (deductible) but flagged so you can see personal-use claims separately at EOFY.'}
                </div>
              </div>
                );
              })()}

              {/* Booked / Finished status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', backgroundColor: formData.status === 'Booked' ? '#e0e7ff' : '#f8fafc', border: '1px solid', borderColor: formData.status === 'Booked' ? '#818cf8' : '#e2e8f0', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                <select
                  value={formData.status || 'Finished'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  data-testid="purchase-status-select"
                  style={{ padding: '0.4rem 0.6rem', borderRadius: '0.375rem', border: '1px solid #cbd5e1', fontWeight: 600 }}
                >
                  <option value="Finished">Finished</option>
                  <option value="Booked">Booked (provisional)</option>
                </select>
                <div style={{ flex: 1, fontSize: '0.75rem', color: '#64748b' }}>
                  {formData.status === 'Booked'
                    ? 'Provisional — this purchase is NOT counted in P&L until you change status to Finished. Use for pre-orders, prepaid shipping labels, etc.'
                    : 'This purchase is fully accounted for and will appear in P&L reports.'}
                </div>
              </div>

              {/* Payment Method — shares the same editable list as Expenses
                  (Settings → Expense Categories → Payment Methods) so all
                  outgoing transactions use the same vocabulary. */}
              <div style={{ marginBottom: '1rem' }}>
                <Label style={{ display: 'block', marginBottom: '0.35rem' }}>Payment Method</Label>
                <select
                  value={formData.payment_method || 'Bank Transfer'}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  data-testid="purchase-payment-method-select"
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '0.375rem', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', fontSize: '0.9rem' }}
                >
                  {(settings?.expense_payment_methods || ['Cash', 'Card', 'Bank Transfer', 'Direct Debit', 'Credit Card', 'Other']).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', marginBottom: 0 }}>
                  Add new payment methods (e.g. American Express) in Settings → Dropdown Values → Payment Methods.
                </p>
              </div>

              {/* Line Items Section */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Purchase Items</h4>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddLineItem}>
                    + Add Item
                  </Button>
                </div>

                {formData.line_items.length === 0 ? (
                  <div style={{ 
                    padding: '2rem', 
                    textAlign: 'center', 
                    backgroundColor: '#f9fafb', 
                    borderRadius: '0.5rem',
                    border: '1px dashed #d1d5db'
                  }}>
                    <p style={{ margin: 0, color: '#6b7280' }}>
                      No items added yet. Click "Add Item" to add stock items to this purchase.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {formData.line_items.map((lineItem, index) => (
                      <div 
                        key={index}
                        style={{ 
                          padding: '1rem', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '0.5rem',
                          backgroundColor: '#f9fafb'
                        }}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '0.75rem', alignItems: 'end' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                              Stock Item *
                            </label>
                            <StockItemTypeahead
                              value={lineItem.stock_item_id}
                              itemName={lineItem.stock_item_name}
                              items={stockItems}
                              onSelect={(it) => {
                                if (it) {
                                  handleLineItemChange(index, 'stock_item_id', it.id);
                                } else {
                                  handleLineItemChange(index, 'stock_item_id', '');
                                  const newLineItems = [...formData.line_items];
                                  newLineItems[index].stock_item_name = '';
                                  setFormData({ ...formData, line_items: newLineItems });
                                }
                              }}
                              onAddNew={() => handleOpenAddStockDialog(index)}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                              Quantity *
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              required
                              value={lineItem.quantity}
                              onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                              placeholder="10"
                              style={{ fontSize: '0.875rem' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                              Cost/Unit *
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              required
                              value={lineItem.cost_per_unit}
                              onChange={(e) => handleLineItemChange(index, 'cost_per_unit', e.target.value)}
                              placeholder="120.00"
                              style={{ fontSize: '0.875rem' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                              Total
                            </label>
                            <div style={{ 
                              padding: '0.5rem', 
                              backgroundColor: '#e5e7eb', 
                              borderRadius: '0.375rem',
                              fontWeight: '600',
                              fontSize: '0.875rem'
                            }}>
                              ${lineItem.total_cost.toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveLineItem(index)}
                              style={{ color: '#dc2626' }}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Total Section with GST Breakdown */}
              {formData.line_items.length > 0 && (
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#f0f9ff', 
                  borderRadius: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Subtotal (excl. GST):</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        ${calculateGSTBreakdown().subtotal.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        GST ({formData.gst_percentage}%):
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        ${calculateGSTBreakdown().gstAmount.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ 
                      borderTop: '2px solid #93c5fd', 
                      paddingTop: '0.5rem',
                      marginTop: '0.25rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontSize: '1.125rem', fontWeight: '600' }}>Total (incl. GST):</span>
                      <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e40af' }}>
                        ${calculateGSTBreakdown().total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => {
                  setShowAddDialog(false);
                  setEditingPurchaseId(null);
                }}>
                  Cancel
                </Button>
                <Button type="submit">{editingPurchaseId ? 'Update Purchase' : 'Record Purchase'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        {/* View Purchase Details Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <DialogHeader>
              <DialogTitle>Purchase Details</DialogTitle>
            </DialogHeader>
            {selectedPurchase && (
              <div>
                {/* Purchase Header Info */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.5rem'
                }}>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Supplier</label>
                    <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>{selectedPurchase.supplier_name}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Purchase Date</label>
                    <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>
                      {formatDate(selectedPurchase.purchase_date)}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Invoice Number</label>
                    <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>
                      {selectedPurchase.invoice_number || '-'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>GST %</label>
                    <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>
                      {selectedPurchase.gst_percentage || 10}%
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Subtotal (excl. GST)</label>
                    <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>
                      ${selectedPurchase.subtotal?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>GST Amount</label>
                    <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>
                      ${selectedPurchase.gst_amount?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Total (incl. GST)</label>
                    <div style={{ fontWeight: '600', marginTop: '0.25rem', fontSize: '1.125rem', color: '#1e40af' }}>
                      ${selectedPurchase.total_cost.toFixed(2)}
                    </div>
                  </div>
                  {selectedPurchase.notes && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Notes</label>
                      <div style={{ marginTop: '0.25rem' }}>{selectedPurchase.notes}</div>
                    </div>
                  )}
                </div>

                {/* Line Items */}
                <div>
                  <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>Items Purchased</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Cost/Unit</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPurchase.line_items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell style={{ fontWeight: '600' }}>{item.stock_item_name}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>${item.cost_per_unit.toFixed(2)}</TableCell>
                          <TableCell style={{ fontWeight: '600' }}>${item.total_cost.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow style={{ backgroundColor: '#fafafa' }}>
                        <TableCell colSpan={3} style={{ fontWeight: '500', textAlign: 'right', fontSize: '0.875rem' }}>
                          Subtotal (excl. GST):
                        </TableCell>
                        <TableCell style={{ fontWeight: '600' }}>
                          ${selectedPurchase.subtotal?.toFixed(2) || '0.00'}
                        </TableCell>
                      </TableRow>
                      <TableRow style={{ backgroundColor: '#fafafa' }}>
                        <TableCell colSpan={3} style={{ fontWeight: '500', textAlign: 'right', fontSize: '0.875rem' }}>
                          GST ({selectedPurchase.gst_percentage || 10}%):
                        </TableCell>
                        <TableCell style={{ fontWeight: '600' }}>
                          ${selectedPurchase.gst_amount?.toFixed(2) || '0.00'}
                        </TableCell>
                      </TableRow>
                      <TableRow style={{ backgroundColor: '#f0f9ff' }}>
                        <TableCell colSpan={3} style={{ fontWeight: '600', textAlign: 'right' }}>
                          Total (incl. GST):
                        </TableCell>
                        <TableCell style={{ fontWeight: 'bold', fontSize: '1.125rem', color: '#1e40af' }}>
                          ${selectedPurchase.total_cost.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', gap: '0.5rem' }}>
                  <Button
                    variant="outline"
                    style={{ color: '#dc2626' }}
                    onClick={() => {
                      setPurchaseToDelete(selectedPurchase);
                      setShowDeleteDialog(true);
                    }}
                    data-testid="view-delete-purchase-btn"
                  >
                    Delete
                  </Button>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                      variant="outline"
                      onClick={() => handleEditPurchase(selectedPurchase)}
                      data-testid="view-edit-purchase-btn"
                    >
                      Edit
                    </Button>
                    <Button onClick={() => setShowViewDialog(false)}>Close</Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add New Stock Item Dialog */}
        <Dialog open={showAddStockDialog} onOpenChange={setShowAddStockDialog}>
          <DialogContent style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <DialogHeader>
              <DialogTitle>Add New Stock Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitNewStock}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Supplier <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(optional)</span>
                  </label>
                  <select
                    value={newStockFormData.supplier_id}
                    onChange={(e) => setNewStockFormData({ ...newStockFormData, supplier_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem'
                    }}
                  >
                    <option value="">-- No Supplier --</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Brand
                    </label>
                    <Input
                      value={newStockFormData.brand}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, brand: e.target.value })}
                      placeholder="e.g., Samsung"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Item Name *
                    </label>
                    <Input
                      required
                      value={newStockFormData.item_name}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, item_name: e.target.value })}
                      placeholder="e.g., 1TB SSD"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Item Code
                    </label>
                    <Input
                      value={newStockFormData.item_code}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, item_code: e.target.value })}
                      placeholder="e.g., SSD-1TB-001"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      SKU
                    </label>
                    <Input
                      value={newStockFormData.sku}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, sku: e.target.value })}
                      placeholder="e.g., SKU12345"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Description
                  </label>
                  <Input
                    value={newStockFormData.description}
                    onChange={(e) => setNewStockFormData({ ...newStockFormData, description: e.target.value })}
                    placeholder="Brief description"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Category
                    </label>
                    <Input
                      value={newStockFormData.category}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, category: e.target.value })}
                      placeholder="e.g., Storage"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Unit of Measure *
                    </label>
                    <Input
                      required
                      value={newStockFormData.unit_of_measure}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, unit_of_measure: e.target.value })}
                      placeholder="e.g., Each, Box, Kg"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Cost Price *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      required
                      value={newStockFormData.cost_price}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, cost_price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Sell Price *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      required
                      value={newStockFormData.sell_price}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, sell_price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Initial Quantity
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newStockFormData.current_quantity}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, current_quantity: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Low Stock Alert
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newStockFormData.low_stock_threshold}
                      onChange={(e) => setNewStockFormData({ ...newStockFormData, low_stock_threshold: parseFloat(e.target.value) || 0 })}
                      placeholder="5"
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <Button type="button" variant="outline" onClick={() => setShowAddStockDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Stock Item</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add New Supplier Dialog */}
        <Dialog open={showAddSupplierDialog} onOpenChange={setShowAddSupplierDialog}>
          <DialogContent style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <DialogHeader>
              <DialogTitle>Add New Supplier</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitNewSupplier}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Supplier Name *
                  </label>
                  <Input
                    required
                    value={newSupplierFormData.name}
                    onChange={(e) => setNewSupplierFormData({ ...newSupplierFormData, name: e.target.value })}
                    placeholder="e.g., Tech Parts Co"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Contact Person
                    </label>
                    <Input
                      value={newSupplierFormData.contact_person}
                      onChange={(e) => setNewSupplierFormData({ ...newSupplierFormData, contact_person: e.target.value })}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Phone
                    </label>
                    <Input
                      value={newSupplierFormData.phone}
                      onChange={(e) => setNewSupplierFormData({ ...newSupplierFormData, phone: e.target.value })}
                      placeholder="e.g., 0400 000 000"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Email
                  </label>
                  <Input
                    type="email"
                    value={newSupplierFormData.email}
                    onChange={(e) => setNewSupplierFormData({ ...newSupplierFormData, email: e.target.value })}
                    placeholder="e.g., contact@supplier.com"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Address
                  </label>
                  <Input
                    value={newSupplierFormData.address}
                    onChange={(e) => setNewSupplierFormData({ ...newSupplierFormData, address: e.target.value })}
                    placeholder="e.g., 123 Supply Street, Sydney NSW"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      ABN
                    </label>
                    <Input
                      value={newSupplierFormData.abn}
                      onChange={(e) => setNewSupplierFormData({ ...newSupplierFormData, abn: e.target.value })}
                      placeholder="e.g., 12 345 678 901"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Payment Terms
                    </label>
                    <Input
                      value={newSupplierFormData.payment_terms}
                      onChange={(e) => setNewSupplierFormData({ ...newSupplierFormData, payment_terms: e.target.value })}
                      placeholder="e.g., Net 30"
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <Button type="button" variant="outline" onClick={() => setShowAddSupplierDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Supplier</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Purchase Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this purchase?</AlertDialogTitle>
              <AlertDialogDescription>
                {purchaseToDelete ? (
                  <>
                    This will permanently delete the purchase from <strong>{purchaseToDelete.supplier_name}</strong>
                    {purchaseToDelete.invoice_number ? ` (Invoice ${purchaseToDelete.invoice_number})` : ''} totalling <strong>${(purchaseToDelete.total_cost || 0).toFixed(2)}</strong>.
                    Stock quantities added by this purchase will be reverted. This cannot be undone.
                  </>
                ) : 'This action cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { setShowDeleteDialog(false); setPurchaseToDelete(null); }}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeletePurchase}
                style={{ backgroundColor: '#dc2626', color: 'white' }}
                data-testid="confirm-delete-purchase-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  };

  // ===== STOCK/INVENTORY PAGE COMPONENT =====
  const StockPage = () => {
    const [stockItems, setStockItems] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    // Feb 2026 — Stock allocation drill-in. See backend
    // GET /api/stock-items/allocations-summary + /{id}/allocations.
    const [allocSummary, setAllocSummary] = useState({}); // {stock_item_id: {active_qty, active_jobs}}
    const [allocExpanded, setAllocExpanded] = useState({}); // {stock_item_id: true}
    const [allocData, setAllocData] = useState({}); // {stock_item_id: {rows, include_history}}
    const [allocLoading, setAllocLoading] = useState({}); // {stock_item_id: true}
    const [formData, setFormData] = useState({
      brand: '',
      item_name: '',
      item_code: '',
      sku: '',
      description: '',
      category: '',
      unit_of_measure: 'Each',
      current_quantity: 0,
      low_stock_threshold: 5,
      supplier_id: ''
    });

    useEffect(() => {
      fetchData();
    }, []);

    const fetchData = async () => {
      try {
        const [stockResponse, suppliersResponse, allocResponse] = await Promise.all([
          axios.get(`${API}/stock-items`),
          axios.get(`${API}/suppliers`),
          axios.get(`${API}/stock-items/allocations-summary`).catch(() => ({ data: {} })),
        ]);
        setStockItems(stockResponse.data);
        setSuppliers(suppliersResponse.data);
        setAllocSummary(allocResponse.data || {});
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load stock items');
      } finally {
        setLoading(false);
      }
    };

    // Feb 2026 — Toggle expanded state for a stock row. Lazily fetches
    // allocation detail the first time a row is opened.
    const toggleAllocRow = async (stockItemId, includeHistory = false) => {
      const currentlyOpen = !!allocExpanded[stockItemId];
      // If closing, just collapse.
      if (currentlyOpen && !includeHistory) {
        setAllocExpanded(prev => ({ ...prev, [stockItemId]: false }));
        return;
      }
      // Open (and/or refetch with new include_history flag).
      setAllocExpanded(prev => ({ ...prev, [stockItemId]: true }));
      setAllocLoading(prev => ({ ...prev, [stockItemId]: true }));
      try {
        const resp = await axios.get(`${API}/stock-items/${stockItemId}/allocations`, {
          params: { include_history: includeHistory },
        });
        setAllocData(prev => ({ ...prev, [stockItemId]: { ...resp.data, include_history: includeHistory } }));
      } catch (error) {
        console.error('Failed to fetch allocations', error);
        toast.error('Failed to load stock allocations');
      } finally {
        setAllocLoading(prev => ({ ...prev, [stockItemId]: false }));
      }
    };

    const handleAdd = () => {
      setFormData({
        brand: '',
        item_name: '',
        item_code: '',
        sku: '',
        description: '',
        category: '',
        unit_of_measure: 'Each',
        current_quantity: 0,
        low_stock_threshold: 5,
        supplier_id: ''
      });
      setShowAddDialog(true);
    };

    const handleEdit = (item) => {
      setSelectedItem(item);
      setFormData({
        brand: item.brand || '',
        item_name: item.item_name,
        item_code: item.item_code || '',
        sku: item.sku || '',
        description: item.description || '',
        category: item.category || '',
        unit_of_measure: item.unit_of_measure || 'Each',
        current_quantity: item.current_quantity,
        low_stock_threshold: item.low_stock_threshold,
        supplier_id: item.supplier_id || ''
      });
      setShowEditDialog(true);
    };

    const handleDelete = async (itemId) => {
      if (!window.confirm('Are you sure you want to delete this stock item?')) {
        return;
      }

      try {
        await axios.delete(`${API}/stock-items/${itemId}`);
        toast.success('Stock item deleted successfully!');
        fetchData();
      } catch (error) {
        console.error('Error deleting stock item:', error);
        toast.error('Failed to delete stock item');
      }
    };

    const handleSubmitAdd = async (e) => {
      e.preventDefault();
      try {
        await axios.post(`${API}/stock-items`, {
          ...formData,
          cost_price: 0,
          sell_price: 0,
          current_quantity: parseFloat(formData.current_quantity),
          low_stock_threshold: parseFloat(formData.low_stock_threshold)
        });
        toast.success('Stock item added successfully!');
        setShowAddDialog(false);
        fetchData();
      } catch (error) {
        console.error('Error adding stock item:', error);
        toast.error('Failed to add stock item');
      }
    };

    const handleSubmitEdit = async (e) => {
      e.preventDefault();
      try {
        await axios.put(`${API}/stock-items/${selectedItem.id}`, {
          ...formData,
          current_quantity: parseFloat(formData.current_quantity),
          low_stock_threshold: parseFloat(formData.low_stock_threshold)
        });
        toast.success('Stock item updated successfully!');
        setShowEditDialog(false);
        fetchData();
      } catch (error) {
        console.error('Error updating stock item:', error);
        toast.error('Failed to update stock item');
      }
    };

    if (loading) {
      return <div className="loading">Loading stock items...</div>;
    }

    const lowStockItems = stockItems.filter(item => 
      item.current_quantity <= item.low_stock_threshold && item.current_quantity > 0
    );
    const outOfStockItems = stockItems.filter(item => item.current_quantity === 0);

    return (
      <div className="stock-page">
        <div className="page-header">
          <div>
            <h2>Stock Inventory Management</h2>
            {(lowStockItems.length > 0 || outOfStockItems.length > 0) && (
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                {outOfStockItems.length > 0 && (
                  <span style={{ color: '#dc2626', fontWeight: '600' }}>
                    ⚠️ {outOfStockItems.length} Out of Stock
                  </span>
                )}
                {lowStockItems.length > 0 && (
                  <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                    ⚠️ {lowStockItems.length} Low Stock
                  </span>
                )}
              </div>
            )}
          </div>
          <Button onClick={handleAdd}>
            + Add Stock Item
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Stock Items ({(() => {
              const s = searchTerm.trim().toLowerCase();
              if (!s) return stockItems.length;
              return stockItems.filter(i =>
                (i.item_name || '').toLowerCase().includes(s) ||
                (i.brand || '').toLowerCase().includes(s) ||
                (i.item_code || '').toLowerCase().includes(s) ||
                (i.sku || '').toLowerCase().includes(s) ||
                (i.category || '').toLowerCase().includes(s) ||
                (i.description || '').toLowerCase().includes(s)
              ).length;
            })()})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="search-container" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <Input
                  type="text"
                  placeholder="Search by name, brand, item code, SKU, category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="stock-search-input"
                />
              </div>
              {searchTerm && (
                <Button
                  variant="outline"
                  onClick={() => setSearchTerm('')}
                  data-testid="stock-clear-search-btn"
                >
                  Clear
                </Button>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: '32px' }}></TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Item Name</TableHead>
                  <TableHead>Item Code</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead title="Sum of quantity across active (non-terminal) jobs that currently have this stock allocated.">On Jobs</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const s = searchTerm.trim().toLowerCase();
                  const base = !s ? stockItems : stockItems.filter(i =>
                    (i.item_name || '').toLowerCase().includes(s) ||
                    (i.brand || '').toLowerCase().includes(s) ||
                    (i.item_code || '').toLowerCase().includes(s) ||
                    (i.sku || '').toLowerCase().includes(s) ||
                    (i.category || '').toLowerCase().includes(s) ||
                    (i.description || '').toLowerCase().includes(s)
                  );
                  // Feb 2026 — Sort alphabetically by item_name (case-insensitive)
                  const visible = [...base].sort((a, b) =>
                    (a.item_name || '').toLowerCase().localeCompare((b.item_name || '').toLowerCase())
                  );
                  if (visible.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={10} style={{ textAlign: 'center', padding: '2rem' }}>
                          {searchTerm
                            ? `No stock items match "${searchTerm}".`
                            : 'No stock items found. Click "Add Stock Item" to create your first item.'}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return visible.map((item) => {
                    const isOutOfStock = item.current_quantity === 0;
                    const isLowStock = item.current_quantity <= item.low_stock_threshold && item.current_quantity > 0;
                    const alloc = allocSummary[item.id] || { active_qty: 0, active_jobs: 0 };
                    const isOpen = !!allocExpanded[item.id];
                    const expandData = allocData[item.id];
                    const isLoading = !!allocLoading[item.id];

                    return (
                      <Fragment key={item.id}>
                      <TableRow
                        style={{
                          backgroundColor: isOutOfStock ? '#fee2e2' : isLowStock ? '#fef3c7' : 'transparent'
                        }}
                      >
                        <TableCell style={{ textAlign: 'center', padding: '0.25rem' }}>
                          <button
                            type="button"
                            onClick={() => toggleAllocRow(item.id, false)}
                            disabled={alloc.active_qty === 0 && !isOpen}
                            data-testid={`stock-alloc-toggle-${item.id}`}
                            title={alloc.active_qty > 0 ? 'Show jobs holding this stock' : 'No active allocations'}
                            style={{
                              background: 'transparent', border: 'none',
                              cursor: alloc.active_qty === 0 && !isOpen ? 'default' : 'pointer',
                              color: alloc.active_qty === 0 && !isOpen ? '#cbd5e1' : '#475569',
                              fontFamily: 'monospace', fontSize: '0.85rem',
                              padding: '0.25rem 0.5rem',
                            }}
                          >
                            {isOpen ? '▼' : '▶'}
                          </button>
                        </TableCell>
                        <TableCell>
                          {isOutOfStock ? (
                            <span style={{ 
                              backgroundColor: '#dc2626', 
                              color: 'white', 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              OUT OF STOCK
                            </span>
                          ) : isLowStock ? (
                            <span style={{ 
                              backgroundColor: '#f59e0b', 
                              color: 'white', 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              LOW STOCK
                            </span>
                          ) : (
                            <span style={{ 
                              backgroundColor: '#10b981', 
                              color: 'white', 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              IN STOCK
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{item.brand || '-'}</TableCell>
                        <TableCell style={{ fontWeight: '600' }}>{item.item_name}</TableCell>
                        <TableCell>{item.item_code || '-'}</TableCell>
                        <TableCell>{item.category || '-'}</TableCell>
                        <TableCell>
                          <span style={{ fontWeight: '600' }}>
                            {item.current_quantity}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.25rem' }}>
                            {item.unit_of_measure}
                          </span>
                        </TableCell>
                        <TableCell>
                          {alloc.active_qty > 0 ? (
                            <span
                              style={{ fontWeight: 600, color: '#7c3aed', cursor: 'pointer' }}
                              onClick={() => toggleAllocRow(item.id, false)}
                              title={`Click to see the ${alloc.active_jobs} job${alloc.active_jobs === 1 ? '' : 's'} holding this stock`}
                            >
                              {alloc.active_qty} <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400 }}>({alloc.active_jobs} job{alloc.active_jobs === 1 ? '' : 's'})</span>
                            </span>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>—</span>
                          )}
                        </TableCell>
                        <TableCell>{item.supplier_name || '-'}</TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(item.id)}
                              style={{ color: '#dc2626' }}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell colSpan={10} style={{ backgroundColor: '#f8fafc', padding: '0.75rem 1.25rem' }}>
                            {isLoading ? (
                              <div style={{ padding: '0.5rem 0', color: '#64748b' }}>Loading allocations…</div>
                            ) : !expandData || expandData.rows.length === 0 ? (
                              <div style={{ padding: '0.5rem 0', color: '#64748b' }}>
                                {expandData?.include_history
                                  ? 'No jobs (active or historical) have ever held this stock item.'
                                  : 'No active jobs are holding this stock. '}
                                {!expandData?.include_history && (
                                  <button
                                    type="button"
                                    onClick={() => toggleAllocRow(item.id, true)}
                                    style={{ background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                  >
                                    Show history (all-time)
                                  </button>
                                )}
                              </div>
                            ) : (
                              <>
                                <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 500 }}>
                                    {expandData.include_history
                                      ? `All-time allocations (${expandData.rows.length})`
                                      : `Active allocations (${expandData.rows.length})`}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => toggleAllocRow(item.id, !expandData.include_history)}
                                    data-testid={`stock-alloc-history-toggle-${item.id}`}
                                    style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: '999px', padding: '0.15rem 0.6rem', fontSize: '0.75rem', color: '#475569', cursor: 'pointer' }}
                                  >
                                    {expandData.include_history ? 'Show active only' : 'Show history (all-time)'}
                                  </button>
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Job #</TableHead>
                                      <TableHead>Customer</TableHead>
                                      <TableHead>Status</TableHead>
                                      <TableHead>Date In</TableHead>
                                      <TableHead className="text-right">Qty</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {expandData.rows.map((r, idx) => {
                                      const statusColor = r.status === 'Finish' ? '#059669'
                                        : r.status === 'Not Recoverable' ? '#dc2626'
                                        : r.status === 'Not Proceeding' ? '#d97706'
                                        : '#475569';
                                      return (
                                        <TableRow
                                          key={`${r.job_id}-${r.line_id || idx}`}
                                          onClick={() => navigate(`/job/${r.job_id}`)}
                                          style={{ cursor: 'pointer', opacity: r.is_active ? 1 : 0.7 }}
                                          className="hover:bg-gray-100"
                                          data-testid={`stock-alloc-job-${r.job_number}`}
                                        >
                                          <TableCell style={{ color: '#2563eb', textDecoration: 'underline' }}>{r.job_number}</TableCell>
                                          <TableCell>{r.customer_name}</TableCell>
                                          <TableCell style={{ color: statusColor, fontWeight: 500 }}>{r.status}</TableCell>
                                          <TableCell>{r.date_in ? new Date(r.date_in).toLocaleDateString('en-AU') : '—'}</TableCell>
                                          <TableCell className="text-right">{r.quantity}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                      </Fragment>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add Stock Item Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <DialogHeader>
              <DialogTitle>Add New Stock Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitAdd}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Brand
                  </label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="e.g., Samsung"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Item Name *
                  </label>
                  <Input
                    required
                    value={formData.item_name}
                    onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                    placeholder="e.g., Galaxy S23 Screen"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Item Code
                  </label>
                  <Input
                    value={formData.item_code}
                    onChange={(e) => setFormData({ ...formData, item_code: e.target.value })}
                    placeholder="e.g., SAM-S23-SCR"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    SKU
                  </label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="e.g., SS23SCR001"
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Description
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., OLED Display for Samsung Galaxy S23"
                    rows={2}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Category
                  </label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Screens"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Unit of Measure
                  </label>
                  <Input
                    value={formData.unit_of_measure}
                    onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                    placeholder="Each"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Current Quantity
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.current_quantity}
                    onChange={(e) => setFormData({ ...formData, current_quantity: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Low Stock Alert Threshold
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.low_stock_threshold}
                    onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                    placeholder="5"
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Supplier <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(optional)</span>
                  </label>
                  <select
                    value={formData.supplier_id}
                    onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem'
                    }}
                  >
                    <option value="">-- No Supplier --</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">Add Stock Item</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Stock Item Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <DialogHeader>
              <DialogTitle>Edit Stock Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitEdit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Brand
                  </label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Item Name *
                  </label>
                  <Input
                    required
                    value={formData.item_name}
                    onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Item Code
                  </label>
                  <Input
                    value={formData.item_code}
                    onChange={(e) => setFormData({ ...formData, item_code: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    SKU
                  </label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Description
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Category
                  </label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Unit of Measure
                  </label>
                  <Input
                    value={formData.unit_of_measure}
                    onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Current Quantity
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.current_quantity}
                    onChange={(e) => setFormData({ ...formData, current_quantity: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Low Stock Alert Threshold
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.low_stock_threshold}
                    onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Supplier
                  </label>
                  <select
                    value={formData.supplier_id}
                    onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem'
                    }}
                  >
                    <option value="">-- Select Supplier --</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">Update Stock Item</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // ===== SUPPLIERS PAGE COMPONENT =====
  const SuppliersPage = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [insights, setInsights] = useState([]);
    const [showInsights, setShowInsights] = useState(false);
    const [formData, setFormData] = useState({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      abn: '',
      payment_terms: ''
    });

    useEffect(() => {
      fetchSuppliers();
      fetchInsights();
    }, []);

    // Deep-link from Global Search (/suppliers?highlight=<id>) → open the supplier in edit dialog
    const supplierLocation = useLocation();
    useEffect(() => {
      const params = new URLSearchParams(supplierLocation.search);
      const highlightId = params.get('highlight');
      if (highlightId && suppliers.length > 0) {
        const found = suppliers.find(s => s.id === highlightId);
        if (found) handleEdit(found);
      }
      // eslint-disable-next-line
    }, [supplierLocation.search, suppliers]);

    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${API}/suppliers`);
        setSuppliers(response.data);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        toast.error('Failed to load suppliers');
      } finally {
        setLoading(false);
      }
    };

    const fetchInsights = async () => {
      try {
        const response = await axios.get(`${API}/suppliers/with-insights`);
        setInsights(response.data || []);
      } catch (error) {
        console.error('Error fetching supplier insights:', error);
      }
    };

    const handleAdd = () => {
      setFormData({
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        abn: '',
        payment_terms: ''
      });
      setShowAddDialog(true);
    };

    const handleEdit = (supplier) => {
      setSelectedSupplier(supplier);
      setFormData({
        name: supplier.name,
        contact_person: supplier.contact_person || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        abn: supplier.abn || '',
        payment_terms: supplier.payment_terms || ''
      });
      setShowEditDialog(true);
    };

    const handleDelete = async (supplierId) => {
      if (!window.confirm('Are you sure you want to delete this supplier?')) {
        return;
      }

      try {
        await axios.delete(`${API}/suppliers/${supplierId}`);
        toast.success('Supplier deleted successfully!');
        fetchSuppliers();
      } catch (error) {
        console.error('Error deleting supplier:', error);
        toast.error('Failed to delete supplier');
      }
    };

    const handleSubmitAdd = async (e) => {
      e.preventDefault();
      try {
        await axios.post(`${API}/suppliers`, formData);
        toast.success('Supplier added successfully!');
        setShowAddDialog(false);
        fetchSuppliers();
      } catch (error) {
        console.error('Error adding supplier:', error);
        toast.error('Failed to add supplier');
      }
    };

    const handleSubmitEdit = async (e) => {
      e.preventDefault();
      try {
        await axios.put(`${API}/suppliers/${selectedSupplier.id}`, formData);
        toast.success('Supplier updated successfully!');
        setShowEditDialog(false);
        fetchSuppliers();
      } catch (error) {
        console.error('Error updating supplier:', error);
        toast.error('Failed to update supplier');
      }
    };

    if (loading) {
      return <div className="loading">Loading suppliers...</div>;
    }

    return (
      <div className="suppliers-page">
        <div className="page-header">
          <h2>Suppliers Management</h2>
          <Button onClick={handleAdd}>
            + Add Supplier
          </Button>
        </div>

        {/* Spend Insights — Business vs Internal */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader style={{ cursor: 'pointer' }} onClick={() => setShowInsights(!showInsights)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <CardTitle>Supplier Spend Insights</CardTitle>
                <CardDescription>Total spend per supplier across all expenses & purchases — split by Business vs Internal/Personal</CardDescription>
              </div>
              <Button variant="outline" size="sm" data-testid="toggle-supplier-insights">
                {showInsights ? 'Hide' : 'Show'} Insights
              </Button>
            </div>
          </CardHeader>
          {showInsights && (
            <CardContent>
              {insights.length === 0 ? (
                <div style={{ padding: '1rem', color: '#64748b' }}>No supplier spend data yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Business Spend</TableHead>
                      <TableHead className="text-right">Internal / Personal</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Transactions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {insights.filter(i => i.total_spend > 0).map((row) => {
                      const internalPct = row.total_spend > 0 ? ((row.internal_total / row.total_spend) * 100) : 0;
                      return (
                        <TableRow key={row.supplier_id} data-testid={`supplier-insight-${row.supplier_id}`}>
                          <TableCell style={{ fontWeight: 600 }}>{row.supplier_name}</TableCell>
                          <TableCell className="text-right" style={{ color: '#16a34a' }}>
                            ${row.business_total.toFixed(2)}
                            <span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: '0.3rem' }}>({row.business_count})</span>
                          </TableCell>
                          <TableCell className="text-right" style={{ color: row.internal_total > 0 ? '#92400e' : '#cbd5e1' }}>
                            ${row.internal_total.toFixed(2)}
                            <span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: '0.3rem' }}>({row.internal_count})</span>
                            {internalPct > 0 && (
                              <span style={{ fontSize: '0.7rem', color: '#92400e', marginLeft: '0.3rem' }}>· {internalPct.toFixed(0)}%</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right" style={{ fontWeight: 600 }}>${row.total_spend.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{row.total_count}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All Suppliers ({(() => {
              const s = searchTerm.trim().toLowerCase();
              if (!s) return suppliers.length;
              return suppliers.filter(sup =>
                (sup.name || '').toLowerCase().includes(s) ||
                (sup.contact_person || '').toLowerCase().includes(s) ||
                (sup.phone || '').toLowerCase().includes(s) ||
                (sup.email || '').toLowerCase().includes(s) ||
                (sup.abn || '').toLowerCase().includes(s)
              ).length;
            })()})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="search-container" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <Input
                  type="text"
                  placeholder="Search by name, contact, phone, email, or ABN..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="supplier-search-input"
                />
              </div>
              {searchTerm && (
                <Button
                  variant="outline"
                  onClick={() => setSearchTerm('')}
                  data-testid="supplier-clear-search-btn"
                >
                  Clear
                </Button>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>ABN</TableHead>
                  <TableHead>Payment Terms</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const s = searchTerm.trim().toLowerCase();
                  const base = !s ? suppliers : suppliers.filter(sup =>
                    (sup.name || '').toLowerCase().includes(s) ||
                    (sup.contact_person || '').toLowerCase().includes(s) ||
                    (sup.phone || '').toLowerCase().includes(s) ||
                    (sup.email || '').toLowerCase().includes(s) ||
                    (sup.abn || '').toLowerCase().includes(s)
                  );
                  // Feb 2026 — Sort alphabetically by name (case-insensitive)
                  const visible = [...base].sort((a, b) =>
                    (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
                  );
                  if (visible.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                          {searchTerm
                            ? `No suppliers match "${searchTerm}".`
                            : 'No suppliers found. Click "Add Supplier" to create your first supplier.'}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return visible.map((supplier) => (
                    <TableRow key={supplier.id}>
                      <TableCell style={{ fontWeight: '600' }}>{supplier.name}</TableCell>
                      <TableCell>{supplier.contact_person || '-'}</TableCell>
                      <TableCell>{supplier.phone || '-'}</TableCell>
                      <TableCell>{supplier.email || '-'}</TableCell>
                      <TableCell>{supplier.abn || '-'}</TableCell>
                      <TableCell>{supplier.payment_terms || '-'}</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(supplier)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(supplier.id)}
                            style={{ color: '#dc2626' }}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add Supplier Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Supplier</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitAdd}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Supplier Name *
                  </label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Tech Parts Co"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Contact Person
                  </label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    placeholder="e.g., John Smith"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Phone
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="e.g., 0400 123 456"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g., contact@supplier.com"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Address
                  </label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="e.g., 123 Tech Street, Sydney NSW 2000"
                    rows={2}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    ABN
                  </label>
                  <Input
                    value={formData.abn}
                    onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                    placeholder="e.g., 12 345 678 901"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Payment Terms
                  </label>
                  <Input
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    placeholder="e.g., Net 30"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">Add Supplier</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Supplier Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Supplier</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitEdit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Supplier Name *
                  </label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Contact Person
                  </label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Phone
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Address
                  </label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    ABN
                  </label>
                  <Input
                    value={formData.abn}
                    onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Payment Terms
                  </label>
                  <Input
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">Update Supplier</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // ===== EXPENSES PAGE COMPONENT =====
  const ExpensesPage = () => {
    const [expenses, setExpenses] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [settings, setSettings] = useState(null);

    // Feb 2026 — receipts now live in Emergent Object Storage and are
    // served through the backend at /api/expenses/{id}/receipt/download
    // (auth-required). Since <a href target="_blank"> can't attach an
    // Authorization header, we pass the token as ?auth=<jwt> so the
    // browser can still open the file in a new tab. Legacy receipt URLs
    // pointing to /api/uploads/receipts/... are passed through unchanged
    // (they're already broken on production because the local disk was
    // wiped on redeploy, but they still work in preview).
    const buildReceiptViewUrl = (receipt_url) => {
      if (!receipt_url) return '#';
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token') || '';
      // Absolute URLs (very old records) — leave as-is.
      const isAbsolute = /^https?:\/\//i.test(receipt_url);
      const url = isAbsolute ? receipt_url : `${backend}${receipt_url}`;
      // Only append auth for our backend-served download endpoint.
      if (url.includes('/api/expenses/') && url.includes('/receipt/download')) {
        const sep = url.includes('?') ? '&' : '?';
        return `${url}${sep}auth=${encodeURIComponent(token)}`;
      }
      return url;
    };
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [expenseToDelete, setExpenseToDelete] = useState(null);
    const [receiptFile, setReceiptFile] = useState(null);
    const [uploadingReceipt, setUploadingReceipt] = useState(false);
    const [showAddSupplierDialog, setShowAddSupplierDialog] = useState(false);
    const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [supplierSearch, setSupplierSearch] = useState(''); // Track supplier search for filtering
    
    const [supplierFormData, setSupplierFormData] = useState({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: ''
    });
    const [filters, setFilters] = useState({
      category: '',
      supplier_id: '',
      date_from: '',
      date_to: ''
    });
    // New uniform date range picker state (matches Reports / Customers / Purchases)
    const [expDateRange, setExpDateRange] = useState('all');
    const [expCustomStart, setExpCustomStart] = useState('');
    const [expCustomEnd, setExpCustomEnd] = useState('');
    // Filter typeahead state (Category + Supplier) — matches Purchases page pattern
    const [filterCategorySearch, setFilterCategorySearch] = useState('');
    const [showFilterCategoryDropdown, setShowFilterCategoryDropdown] = useState(false);
    const [filterSupplierSearch, setFilterSupplierSearch] = useState('');
    const [showFilterSupplierDropdown, setShowFilterSupplierDropdown] = useState(false);
    // Internal filter: 'all' | 'business' | 'internal'
    const [internalFilter, setInternalFilter] = useState('all');
    // Payment-method click-to-filter (from the Payment Methods Breakdown card).
    // Setting this narrows the transactions table to just that method —
    // designed for month-end reconciliation against bank/credit-card statements.
    const [pmFilter, setPmFilter] = useState(null);
    // Reconciliation filter — when true, hides rows the user has already
    // ticked as reconciled. Lets them focus on remaining rows to work through.
    const [unreconciledOnly, setUnreconciledOnly] = useState(false);

    // Client-side derived list combining pmFilter + unreconciledOnly on top
    // of whatever the server already filtered. Keeps the table + counts in
    // sync so the "No results" message and empty-state make sense.
    const visibleExpenses = expenses
      .filter(e => !pmFilter || (e.payment_method || 'Unspecified') === pmFilter)
      .filter(e => !unreconciledOnly || !e.reconciled);

    // Toggle the reconciled flag on a single expense. Optimistically updates
    // local state before the request lands so the checkbox feels instant.
    const toggleReconciled = async (expenseId, currentValue) => {
      const nextValue = !currentValue;
      // Optimistic update
      setExpenses(prev => prev.map(e => e.id === expenseId
        ? { ...e, reconciled: nextValue, reconciled_at: nextValue ? new Date().toISOString() : null }
        : e));
      try {
        await axios.patch(`${API}/expenses/${expenseId}/reconciled`, { reconciled: nextValue });
      } catch (err) {
        // Roll back on failure
        setExpenses(prev => prev.map(e => e.id === expenseId
          ? { ...e, reconciled: currentValue }
          : e));
        toast.error(err?.response?.data?.detail || 'Failed to update reconciled status');
      }
    };
    const [formData, setFormData] = useState({
      date: todayLocalISO(),
      description: '',
      category: '',
      supplier_id: '',
      supplier_name: '',
      amount_ex_gst: '',
      gst_amount: '',
      total_amount: '',
      payment_method: '',
      reference: '',
      notes: ''
    });

    // Feb 2026 — Multi-line "extra transactions" for repeating expenses
    // (Google Ads, owner drawings, etc). Each extra line becomes its own
    // Expense record on submit via POST /api/expenses/bulk. NOTE: state
    // now lives INSIDE ExpenseForm (see below) so mutating extras doesn't
    // re-render the parent Expenses component and remount the form.
    // We keep a ref at parent level so handleSubmitAdd can read the extras
    // at submit time.
    const extraLinesRef = useRef([]);
    const resetExtraLines = () => { extraLinesRef.current = []; };

    // Common expense descriptions for dropdown
    // Build common descriptions from expense categories + some generic items
    const baseDescriptions = [
      'Electricity Bill',
      'Gas Bill',
      'Water Bill',
      'Internet Service',
      'Phone Bill',
      'Office Supplies',
      'Fuel - Vehicle',
      'Vehicle Maintenance',
      'Equipment Purchase',
      'Software Subscription',
      'Insurance Premium',
      'Rent Payment',
      'Rates Payment',
      'Council Rates',
      'Professional Services',
      'Advertising/Marketing',
      'Bank Fees',
      'Postage/Shipping',
      'Cleaning Services',
      'Office Rent',
      'Travel Expenses',
      'Staff Training',
      'Strata Fees',
      'Repairs & Maintenance'
    ];
    
    // Add category names from settings as additional description options
    const categoryDescriptions = settings?.expense_categories_with_codes?.length > 0
      ? settings.expense_categories_with_codes.map(cat => cat.name)
      : (settings?.expense_categories || []);
    
    // Combine and remove duplicates
    const commonDescriptions = [...new Set([...baseDescriptions, ...categoryDescriptions])].sort();

    // Aug 2026 — Recent descriptions the user has actually typed on past
    // expenses. These get prepended to the suggestion list so the typeahead
    // learns their patterns (e.g. "Optus mobile", "Voipline landline", etc.).
    // Newest first, deduped, limited to 20 to keep the dropdown compact.
    const recentDescriptions = useMemo(() => {
      const seen = new Set();
      const result = [];
      const sorted = [...(expenses || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      for (const e of sorted) {
        const d = (e.description || '').trim();
        if (!d) continue;
        const k = d.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        result.push(d);
        if (result.length >= 20) break;
      }
      return result;
    }, [expenses]);

    // Combined suggestion pool: recent first, then common (deduped).
    const allDescriptionSuggestions = useMemo(() => {
      const seen = new Set();
      const out = [];
      for (const d of [...recentDescriptions, ...commonDescriptions]) {
        const k = d.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(d);
      }
      return out;
    }, [recentDescriptions, commonDescriptions]);

    // Description typeahead state — Aug 2026 (v3 — simple + stable).
    // Reverted to the original Select + Input pattern (which never had the
    // one-letter-at-a-time bug) but the Select now shows a "Recent" group at
    // the top with your last 20 typed descriptions, plus the "Common" list.
    // Typing in the input is uncontrolled (defaultValue + ref) so keystrokes
    // don't trigger parent re-renders. `filteredDescriptions` / `descQuery`
    // / `descInputRef` / `showDescDropdown` from the v2 attempt are gone.

    // Use refs for text inputs to avoid re-renders on every keystroke
    const descriptionRef = useRef(null);
    const amountExGstRef = useRef(null);
    const gstAmountRef = useRef(null);
    const totalAmountRef = useRef(null);
    const referenceRef = useRef(null);
    const notesRef = useRef(null);

    useEffect(() => {
      fetchData();
    }, []);

    const fetchData = async () => {
      try {
        const [expensesRes, suppliersRes, settingsRes] = await Promise.all([
          axios.get(`${API}/expenses`),
          axios.get(`${API}/suppliers`),
          axios.get(`${API}/settings`)
        ]);
        setExpenses(expensesRes.data);
        setSuppliers(suppliersRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load expenses');
      } finally {
        setLoading(false);
      }
    };

    const fetchExpenses = async () => {
      try {
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
        if (internalFilter && internalFilter !== 'all') params.append('internal_filter', internalFilter);
        const { start, end } = getDateBoundaries(expDateRange, expCustomStart, expCustomEnd);
        if (start) params.append('start_date', start);
        if (end) params.append('end_date', end);
        
        const response = await axios.get(`${API}/expenses?${params.toString()}`);
        setExpenses(response.data);
      } catch (error) {
        console.error('Error fetching expenses:', error);
        toast.error('Failed to load expenses');
      }
    };

    // Auto-refetch when filters or the uniform date range change
    useEffect(() => {
      fetchExpenses();
      // eslint-disable-next-line
    }, [expDateRange, expCustomStart, expCustomEnd, filters.category, filters.supplier_id, internalFilter]);

    // Feb 2026 — Deep-link from Reports → Expense Report (?highlight=<id>).
    // Scrolls to the expense row and briefly glows it so the user can spot it.
    const expenseLocation = useLocation();
    const [highlightExpenseId, setHighlightExpenseId] = useState(null);
    useEffect(() => {
      const params = new URLSearchParams(expenseLocation.search);
      const highlightId = params.get('highlight');
      if (!highlightId) return;
      // Widen filters temporarily to guarantee the row is present.
      if (expDateRange !== 'all') setExpDateRange('all');
      setInternalFilter('all');
      // Wait for expenses to be re-fetched, then scroll + glow.
      setTimeout(() => {
        setHighlightExpenseId(highlightId);
        const el = document.querySelector(`[data-testid="expense-row-${highlightId}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // Clear the glow after 3s so it doesn't linger.
        setTimeout(() => setHighlightExpenseId(null), 3000);
      }, 400);
      // eslint-disable-next-line
    }, [expenseLocation.search]);

    const handleFilterChange = (field, value) => {
      setFilters({ ...filters, [field]: value });
    };

    const handleApplyFilters = () => {
      fetchExpenses();
    };

    const handleClearFilters = () => {
      setFilters({
        category: '',
        supplier_id: '',
        date_from: '',
        date_to: ''
      });
      setExpDateRange('all');
      setExpCustomStart('');
      setExpCustomEnd('');
      setFilterCategorySearch('');
      setFilterSupplierSearch('');
      setInternalFilter('all');
    };

    const resetForm = () => {
      setFormData({
        date: todayLocalISO(),
        description: '',
        category: '',
        supplier_id: '',
        supplier_name: '',
        amount_ex_gst: '',
        gst_amount: '',
        total_amount: '',
        payment_method: settings?.default_payment_method || 'Bank Transfer',
        reference: '',
        notes: '',
        is_internal: false,
        personal_use_claimed: false,
        status: 'Finished'
      });
    };

    const handleAdd = () => {
      resetForm();
      resetExtraLines();
      setShowAddDialog(true);
    };

    const handleEdit = (expense) => {
      setSelectedExpense(expense);
      setFormData({
        date: expense.date,
        description: expense.description,
        category: expense.category,
        supplier_id: expense.supplier_id || '',
        supplier_name: expense.supplier_name || '',
        amount_ex_gst: expense.amount_ex_gst.toString(),
        gst_amount: expense.gst_amount.toString(),
        total_amount: expense.total_amount.toString(),
        payment_method: expense.payment_method,
        reference: expense.reference || '',
        notes: expense.notes || '',
        is_internal: !!expense.is_internal,
        personal_use_claimed: !!expense.personal_use_claimed,
        status: expense.status || 'Finished'
      });
      setShowEditDialog(true);
    };

    // Feb 2026 — Copy an existing expense as a starting point for a new one.
    // Carries supplier / category / description / payment method / GST %/flags
    // (the boring stuff) but blanks amount + reference and defaults date to
    // today, so the user only needs to enter what actually differs.
    // Receipt attachment is intentionally NOT copied — each transaction has
    // its own document.
    const handleCopy = (expense) => {
      resetExtraLines();
      setSelectedExpense(null); // ensure it's treated as a new expense, not an edit
      setFormData({
        date: todayLocalISO(),                              // today by default
        description: expense.description || '',
        category: expense.category || '',
        supplier_id: expense.supplier_id || '',
        supplier_name: expense.supplier_name || '',
        amount_ex_gst: '',                                  // force conscious entry
        gst_amount: '',                                     // force conscious entry
        total_amount: '',                                   // force conscious entry
        payment_method: expense.payment_method || '',
        reference: '',                                      // usually differs each time
        notes: expense.notes || '',
        is_internal: !!expense.is_internal,
        personal_use_claimed: !!expense.personal_use_claimed,
        status: expense.status || 'Finished'
      });
      setShowAddDialog(true);
      toast.info(`Copying from ${expense.supplier_name || 'previous'} expense — please enter date & amount`);
    };

    const handleDeleteClick = (expense) => {
      setExpenseToDelete(expense);
      setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
      if (!expenseToDelete) return;
      
      try {
        await axios.delete(`${API}/expenses/${expenseToDelete.id}`);
        toast.success('Expense deleted successfully!');
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting expense:', error);
        toast.error('Failed to delete expense');
      } finally {
        setDeleteDialogOpen(false);
        setExpenseToDelete(null);
      }
    };

    const handleSupplierChange = (supplierId) => {
      const actualSupplierId = supplierId === 'none' ? '' : supplierId;
      const supplier = suppliers.find(s => s.id === actualSupplierId);
      setFormData({
        ...formData,
        supplier_id: actualSupplierId,
        supplier_name: supplier ? supplier.name : ''
      });
    };

    const calculateGST = (amountExGst) => {
      const amount = parseFloat(amountExGst) || 0;
      const gst = amount * 0.1; // 10% GST
      const total = amount + gst;
      setFormData({
        ...formData,
        amount_ex_gst: amountExGst,
        gst_amount: gst.toFixed(2),
        total_amount: total.toFixed(2)
      });
    };

    const calculateFromTotal = (totalAmount) => {
      const total = parseFloat(totalAmount) || 0;
      const amountExGst = total / 1.1; // Reverse GST calculation
      const gst = total - amountExGst;
      setFormData({
        ...formData,
        amount_ex_gst: amountExGst.toFixed(2),
        gst_amount: gst.toFixed(2),
        total_amount: totalAmount
      });
    };

    const handleSubmitAdd = async (e, dataOverride = null) => {
      e.preventDefault();
      const data = dataOverride || formData;
      
      if (!data.description || !data.category || !data.total_amount) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Feb 2026 — Bulk mode: if extraLines exist, POST all lines (row 1 =
      // main form fields, rows 2..N = extraLines) to /expenses/bulk in one
      // shot. Creates one Expense record per line so reports see them as
      // discrete transactions on their individual dates.
      const extraLines = extraLinesRef.current || [];
      if (extraLines.length > 0) {
        // Validate every extra line first.
        for (let i = 0; i < extraLines.length; i++) {
          const l = extraLines[i];
          if (!l.date) {
            toast.error(`Line ${i + 2}: date is required`);
            return;
          }
          if (!parseFloat(l.total_amount)) {
            toast.error(`Line ${i + 2}: total amount is required`);
            return;
          }
        }
        try {
          const bulkPayload = {
            description:    data.description,
            category:       data.category,
            supplier_id:    data.supplier_id || null,
            supplier_name:  data.supplier_name || '',
            payment_method: data.payment_method || '',
            reference:      data.reference || '',
            notes:          data.notes || '',
            is_internal:          !!data.is_internal,
            personal_use_claimed: !!data.personal_use_claimed,
            lines: [
              // Line 1 = main form
              {
                date: data.date,
                amount_ex_gst: parseFloat(data.amount_ex_gst) || 0,
                gst_amount:    parseFloat(data.gst_amount)    || 0,
                total_amount:  parseFloat(data.total_amount)  || 0,
              },
              // Lines 2..N = the extra rows
              ...extraLines.map((l) => ({
                date: l.date,
                amount_ex_gst: parseFloat(l.amount_ex_gst) || 0,
                gst_amount:    parseFloat(l.gst_amount)    || 0,
                total_amount:  parseFloat(l.total_amount)  || 0,
              })),
            ],
          };
          const bulkRes = await axios.post(`${API}/expenses/bulk`, bulkPayload);
          const count = bulkRes.data?.created_count ?? bulkPayload.lines.length;
          toast.success(`${count} expenses added successfully!`);
          setShowAddDialog(false);
          resetForm();
          resetExtraLines();
          setReceiptFile(null);
          fetchExpenses();
          return;
        } catch (err) {
          console.error('Error bulk-adding expenses:', err);
          toast.error(err.response?.data?.detail || 'Failed to add expenses');
          return;
        }
      }

      try {
        const submitData = {
          ...data,
          amount_ex_gst: parseFloat(data.amount_ex_gst) || 0,
          gst_amount: parseFloat(data.gst_amount) || 0,
          total_amount: parseFloat(data.total_amount) || 0
        };
        const response = await axios.post(`${API}/expenses`, submitData);
        const newExpenseId = response.data.id;
        
        // Upload receipt if one was selected
        if (receiptFile) {
          setUploadingReceipt(true);
          const formDataFile = new FormData();
          formDataFile.append('file', receiptFile);
          try {
            await axios.post(`${API}/expenses/${newExpenseId}/receipt`, formDataFile, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Expense added with receipt!');
          } catch (uploadError) {
            console.error('Error uploading receipt:', uploadError);
            toast.success('Expense added, but receipt upload failed');
          }
          setUploadingReceipt(false);
        } else {
          toast.success('Expense added successfully!');
        }
        
        setShowAddDialog(false);
        resetForm();
        setReceiptFile(null);
        fetchExpenses();
      } catch (error) {
        console.error('Error adding expense:', error);
        toast.error('Failed to add expense');
      }
    };

    const handleSubmitEdit = async (e, dataOverride = null) => {
      e.preventDefault();
      const data = dataOverride || formData;
      
      try {
        const submitData = {
          ...data,
          amount_ex_gst: parseFloat(data.amount_ex_gst) || 0,
          gst_amount: parseFloat(data.gst_amount) || 0,
          total_amount: parseFloat(data.total_amount) || 0
        };
        await axios.put(`${API}/expenses/${selectedExpense.id}`, submitData);
        
        // Upload receipt if one was selected
        if (receiptFile) {
          setUploadingReceipt(true);
          const formDataFile = new FormData();
          formDataFile.append('file', receiptFile);
          try {
            await axios.post(`${API}/expenses/${selectedExpense.id}/receipt`, formDataFile, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Expense updated with receipt!');
          } catch (uploadError) {
            console.error('Error uploading receipt:', uploadError);
            toast.success('Expense updated, but receipt upload failed');
          }
          setUploadingReceipt(false);
        } else {
          toast.success('Expense updated successfully!');
        }
        
        setShowEditDialog(false);
        setReceiptFile(null);
        fetchExpenses();
      } catch (error) {
        console.error('Error updating expense:', error);
        toast.error('Failed to update expense');
      }
    };

    const handleDeleteReceipt = async (expenseId) => {
      try {
        await axios.delete(`${API}/expenses/${expenseId}/receipt`);
        toast.success('Receipt deleted');
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting receipt:', error);
        toast.error('Failed to delete receipt');
      }
    };

    const handleAddSupplier = async (e) => {
      e.preventDefault();
      
      if (!supplierFormData.name) {
        toast.error('Supplier name is required');
        return;
      }
      
      try {
        const response = await axios.post(`${API}/suppliers`, supplierFormData);
        toast.success('Supplier added successfully!');
        
        // Get the new supplier data
        const newSupplier = response.data;
        
        // Refresh suppliers list
        const suppliersRes = await axios.get(`${API}/suppliers`);
        setSuppliers(suppliersRes.data);
        
        // Auto-select the newly created supplier in the expense form
        if (newSupplier && newSupplier.id) {
          setFormData({ ...formData, supplier_id: newSupplier.id, supplier_name: newSupplier.name });
          setSupplierSearch(newSupplier.name);
        }
        
        setShowAddSupplierDialog(false);
        setSupplierFormData({
          name: '',
          contact_person: '',
          email: '',
          phone: '',
          address: ''
        });
      } catch (error) {
        console.error('Error adding supplier:', error);
        toast.error('Failed to add supplier');
      }
    };

    const handleAddCategory = async (e) => {
      e.preventDefault();
      
      if (!newCategoryName.trim()) {
        toast.error('Category name is required');
        return;
      }
      
      try {
        // Get current categories and add the new one (both old and new format)
        const currentCategories = settings?.expense_categories || [];
        const updatedCategories = [...currentCategories, newCategoryName.trim()];
        
        const currentCategoriesWithCodes = settings?.expense_categories_with_codes || [];
        const updatedCategoriesWithCodes = [...currentCategoriesWithCodes, { name: newCategoryName.trim(), code: '' }];
        
        // Update settings (both formats for compatibility)
        await axios.put(`${API}/settings`, { 
          expense_categories: updatedCategories,
          expense_categories_with_codes: updatedCategoriesWithCodes
        });
        
        // Refresh settings
        const settingsRes = await axios.get(`${API}/settings`);
        setSettings(settingsRes.data);
        
        toast.success('Category added successfully!');
        setShowAddCategoryDialog(false);
        setNewCategoryName('');
      } catch (error) {
        console.error('Error adding category:', error);
        toast.error('Failed to add category');
      }
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD'
      }).format(amount);
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    const getTotalExpenses = () => {
      return expenses.reduce((sum, expense) => sum + (expense.total_amount || 0), 0);
    };

    const getTotalGST = () => {
      return expenses.reduce((sum, expense) => sum + (expense.gst_amount || 0), 0);
    };

    // Group filtered expenses by payment_method — powers the Payment Methods
    // Breakdown card. Same shape as the Purchases equivalent.
    const paymentMethodBreakdown = useMemo(() => {
      const groups = new Map();
      for (const e of expenses) {
        const key = (e.payment_method || 'Unspecified').trim() || 'Unspecified';
        const g = groups.get(key) || { method: key, count: 0, subtotal: 0, total: 0 };
        g.count += 1;
        g.subtotal += Number(e.amount_ex_gst || 0);
        g.total += Number(e.total_amount || 0);
        groups.set(key, g);
      }
      const rows = [...groups.values()].sort((a, b) => b.total - a.total);
      const grandTotal = rows.reduce((s, r) => s + r.total, 0);
      return rows.map(r => ({ ...r, pct: grandTotal > 0 ? (r.total / grandTotal) * 100 : 0 }));
    }, [expenses]);

    if (loading) {
      return <div className="loading">Loading expenses...</div>;
    }

    // Get expense categories - prefer new format with codes, fallback to old format
    const expenseCategories = settings?.expense_categories_with_codes?.length > 0
      ? settings.expense_categories_with_codes.map(cat => cat.code ? `${cat.code} - ${cat.name}` : cat.name)
      : (settings?.expense_categories || [
          "Utilities - Electricity",
          "Utilities - Gas",
          "Vehicle - Fuel",
          "Rent/Rates",
          "Office Supplies",
          "Other"
        ]);

    const paymentMethods = settings?.expense_payment_methods || [
      "Cash",
      "Card",
      "Bank Transfer",
      "Direct Debit",
      "Other"
    ];

    const ExpenseForm = ({ onSubmit, submitLabel, allowBulk = false }) => {
      // Local refs for performance - avoid re-renders on every keystroke
      const localDescRef = useRef(null);
      const localAmountRef = useRef(null);
      const localGstRef = useRef(null);
      const localTotalRef = useRef(null);
      const localRefRef = useRef(null);
      const localNotesRef = useRef(null);

      // Feb 2026 — Extras state OWNED BY the form so mutating extras doesn't
      // remount the parent's uncontrolled inputs. Synced to
      // extraLinesRef on every change so parent's handleSubmitAdd can read
      // them at submit time.
      const [extraLines, _setExtraLines] = useState(() => extraLinesRef.current || []);
      const setExtraLines = (updater) => {
        _setExtraLines((prev) => {
          const next = typeof updater === 'function' ? updater(prev) : updater;
          extraLinesRef.current = next;
          return next;
        });
      };
      const makeBlankLine = () => ({
        id: `xl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        date: todayLocalISO(),
        amount_ex_gst: '',
        gst_amount:    '',
        total_amount:  '',
        gst_free: false,
      });
      const addExtraLine    = () => setExtraLines((ls) => [...ls, makeBlankLine()]);
      const removeExtraLine = (id) => setExtraLines((ls) => ls.filter((l) => l.id !== id));
      const patchExtraLine  = (id, patch) => setExtraLines((ls) =>
        ls.map((l) => (l.id === id ? { ...l, ...patch } : l))
      );
      // Fresh-state recalc helpers (functional update — no stale-closure).
      const recalcLineFromTotal = (id, total) => {
        setExtraLines((ls) => ls.map((l) => {
          if (l.id !== id) return l;
          const t = parseFloat(total) || 0;
          if (l.gst_free) return { ...l, total_amount: total, amount_ex_gst: total, gst_amount: '0.00' };
          const ex  = (t / 1.1);
          return { ...l, total_amount: total, amount_ex_gst: ex.toFixed(2), gst_amount: (t - ex).toFixed(2) };
        }));
      };
      const recalcLineFromEx = (id, ex) => {
        setExtraLines((ls) => ls.map((l) => {
          if (l.id !== id) return l;
          const e = parseFloat(ex) || 0;
          if (l.gst_free) return { ...l, amount_ex_gst: ex, gst_amount: '0.00', total_amount: ex };
          const gst = e * 0.1;
          return { ...l, amount_ex_gst: ex, gst_amount: gst.toFixed(2), total_amount: (e + gst).toFixed(2) };
        }));
      };
      const toggleLineGstFree = (id, isFree) => {
        setExtraLines((ls) => ls.map((l) => {
          if (l.id !== id) return l;
          if (isFree) {
            const t = parseFloat(l.total_amount) || 0;
            return { ...l, gst_free: true, gst_amount: '0.00', amount_ex_gst: t ? t.toFixed(2) : '' };
          }
          // OFF → recompute 10% breakdown FROM total (uses the CURRENT line,
          // not a stale closure — functional update guarantees freshness).
          const t = parseFloat(l.total_amount) || 0;
          const ex = (t / 1.1);
          return { ...l, gst_free: false, amount_ex_gst: ex.toFixed(2), gst_amount: (t - ex).toFixed(2) };
        }));
      };

      // Supplier search state
      const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
      const [localSupplierSearch, setLocalSupplierSearch] = useState(formData.supplier_name || '');
      const supplierInputRef = useRef(null);

      // Category search state (typeahead like supplier)
      const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
      const [categorySearch, setCategorySearch] = useState(formData.category || '');
      const categoryInputRef = useRef(null);

      // GST-Free toggle (one-tap for items like Bank Fees, Council Rates)
      const [gstFree, setGstFree] = useState(parseFloat(formData.gst_amount || 0) === 0 && parseFloat(formData.amount_ex_gst || 0) > 0);

      // Internal toggle (personal expense made on business card — excluded from P&L by default)
      const [isInternal, setIsInternal] = useState(!!formData.is_internal);
      // Personal-use CLAIMED (Feb 2026) — kept in P&L (deductible) but flagged
      // as personal-use so the owner can reconcile at EOFY.
      const [isPersonalUseClaimed, setIsPersonalUseClaimed] = useState(!!formData.personal_use_claimed);

      // Booked / Finished status (provisional expenses excluded from P&L until finalised)
      const [expenseStatus, setExpenseStatus] = useState(formData.status || 'Finished');

      // Memoized filtered suppliers
      const filteredSuppliers = useMemo(() => {
        if (!localSupplierSearch) return suppliers;
        const searchLower = localSupplierSearch.toLowerCase();
        return suppliers.filter(supplier => 
          supplier.name.toLowerCase().includes(searchLower)
        );
      }, [suppliers, localSupplierSearch]);

      // Memoized filtered categories (matches code OR name)
      const filteredCategories = useMemo(() => {
        if (!categorySearch) return expenseCategories;
        const searchLower = categorySearch.toLowerCase();
        return expenseCategories.filter(cat =>
          cat.toLowerCase().includes(searchLower)
        );
      }, [expenseCategories, categorySearch]);

      const handleCategorySelect = (cat) => {
        // Capture current ref values before state update (to avoid losing typed values)
        const currentValues = {
          description: localDescRef.current?.value || formData.description,
          amount_ex_gst: localAmountRef.current?.value || formData.amount_ex_gst,
          gst_amount: localGstRef.current?.value || formData.gst_amount,
          total_amount: localTotalRef.current?.value || formData.total_amount,
          reference: localRefRef.current?.value !== undefined ? localRefRef.current.value : formData.reference,
          notes: localNotesRef.current?.value !== undefined ? localNotesRef.current.value : formData.notes
        };
        setFormData({ ...formData, ...currentValues, category: cat });
        setCategorySearch(cat);
        setShowCategoryDropdown(false);
      };

      const handleSupplierSelect = (supplier) => {
        setFormData({ ...formData, supplier_id: supplier.id, supplier_name: supplier.name });
        setLocalSupplierSearch(supplier.name);
        setShowSupplierDropdown(false);
      };

      const handleSupplierClear = () => {
        setFormData({ ...formData, supplier_id: '', supplier_name: '' });
        setLocalSupplierSearch('');
      };

      // Update local supplier search when formData changes (for edit mode)
      useEffect(() => {
        if (localDescRef.current) localDescRef.current.value = formData.description || '';
        if (localAmountRef.current) localAmountRef.current.value = formData.amount_ex_gst || '';
        if (localGstRef.current) localGstRef.current.value = formData.gst_amount || '';
        if (localTotalRef.current) localTotalRef.current.value = formData.total_amount || '';
        if (localRefRef.current) localRefRef.current.value = formData.reference || '';
        if (localNotesRef.current) localNotesRef.current.value = formData.notes || '';
      }, [formData.description, formData.amount_ex_gst, formData.gst_amount, formData.total_amount, formData.reference, formData.notes]);
      
      // Separate effect for supplier to avoid loops
      useEffect(() => {
        if (formData.supplier_name && formData.supplier_name !== localSupplierSearch) {
          setLocalSupplierSearch(formData.supplier_name);
        }
      }, [formData.supplier_name]);

      // Sync category search with formData (for edit mode)
      useEffect(() => {
        if (formData.category && formData.category !== categorySearch) {
          setCategorySearch(formData.category);
        }
      }, [formData.category]);

      const handleLocalSubmit = (e) => {
        e.preventDefault();
        // Collect values from refs before submitting
        const updatedFormData = {
          ...formData,
          description: localDescRef.current?.value || formData.description,
          amount_ex_gst: localAmountRef.current?.value || formData.amount_ex_gst,
          gst_amount: localGstRef.current?.value || formData.gst_amount,
          total_amount: localTotalRef.current?.value || formData.total_amount,
          reference: localRefRef.current?.value !== undefined ? localRefRef.current.value : formData.reference,
          notes: localNotesRef.current?.value !== undefined ? localNotesRef.current.value : formData.notes,
          is_internal: isInternal,
          personal_use_claimed: isPersonalUseClaimed,
          status: expenseStatus
        };
        setFormData(updatedFormData);
        // Pass the updated data directly so submit handler doesn't depend on stale state
        onSubmit(e, updatedFormData);
      };

      const handleTotalChange = (value) => {
        const total = parseFloat(value) || 0;
        const gst = gstFree ? 0 : total / 11;
        const exGst = total - gst;
        if (localAmountRef.current) localAmountRef.current.value = exGst.toFixed(2);
        if (localGstRef.current) localGstRef.current.value = gst.toFixed(2);
      };

      const handleExGstChange = (value) => {
        const exGst = parseFloat(value) || 0;
        const gst = gstFree ? 0 : exGst * 0.1;
        const total = exGst + gst;
        if (localGstRef.current) localGstRef.current.value = gst.toFixed(2);
        if (localTotalRef.current) localTotalRef.current.value = total.toFixed(2);
      };

      const handleToggleGstFree = (checked) => {
        setGstFree(checked);
        const exGst = parseFloat(localAmountRef.current?.value || formData.amount_ex_gst) || 0;
        const total = parseFloat(localTotalRef.current?.value || formData.total_amount) || 0;
        if (checked) {
          // GST-Free: gst = 0, ex-GST equals total
          const baseAmount = exGst > 0 ? exGst : total;
          if (localAmountRef.current) localAmountRef.current.value = baseAmount.toFixed(2);
          if (localGstRef.current) localGstRef.current.value = '0.00';
          if (localTotalRef.current) localTotalRef.current.value = baseAmount.toFixed(2);
        } else {
          // Restore 10% GST from current ex-GST (or back-calc from total if ex-GST is 0)
          const baseExGst = exGst > 0 ? exGst : (total > 0 ? total / 1.1 : 0);
          if (baseExGst > 0) {
            const gst = baseExGst * 0.1;
            if (localAmountRef.current) localAmountRef.current.value = baseExGst.toFixed(2);
            if (localGstRef.current) localGstRef.current.value = gst.toFixed(2);
            if (localTotalRef.current) localTotalRef.current.value = (baseExGst + gst).toFixed(2);
          }
        }
      };

      return (
      <form onSubmit={handleLocalSubmit}>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {/* Row 1: Date and Supplier */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                defaultValue={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div style={{ position: 'relative' }}>
              <Label>Supplier</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  ref={supplierInputRef}
                  value={localSupplierSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLocalSupplierSearch(value);
                    setShowSupplierDropdown(true);
                    
                    if (!value) {
                      setFormData({ ...formData, supplier_id: '', supplier_name: '' });
                    }
                  }}
                  onFocus={() => setShowSupplierDropdown(true)}
                  onBlur={() => {
                    setTimeout(() => setShowSupplierDropdown(false), 300);
                  }}
                  placeholder="Type to search suppliers..."
                  autoComplete="off"
                />
                {localSupplierSearch && (
                  <button
                    type="button"
                    onClick={handleSupplierClear}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      fontSize: '1rem'
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              {showSupplierDropdown && (localSupplierSearch || filteredSuppliers.length > 0) && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 50,
                  marginTop: '4px'
                }}>
                  {filteredSuppliers.length > 0 ? (
                    filteredSuppliers.map((supplier) => (
                      <div
                        key={supplier.id}
                        onClick={() => handleSupplierSelect(supplier)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: formData.supplier_id === supplier.id ? '#eff6ff' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = formData.supplier_id === supplier.id ? '#eff6ff' : 'transparent'}
                      >
                        <div style={{ fontWeight: '500' }}>{supplier.name}</div>
                        {supplier.contact_name && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{supplier.contact_name}</div>
                        )}
                      </div>
                    ))
                  ) : localSupplierSearch ? (
                    <div style={{ padding: '12px' }}>
                      <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '8px' }}>
                        No suppliers found matching "<strong>{localSupplierSearch}</strong>"
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowSupplierDropdown(false);
                          setSupplierFormData({ 
                            name: localSupplierSearch, 
                            contact_person: '', 
                            email: '', 
                            phone: '', 
                            address: '' 
                          });
                          setShowAddSupplierDialog(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>+</span> Create "{localSupplierSearch}" as new supplier
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Category */}
          <div>
            <Label>Category *</Label>
            <div style={{ position: 'relative' }}>
              <Input
                ref={categoryInputRef}
                type="text"
                value={categorySearch}
                placeholder="Type code or name (e.g. 6401 or Council)"
                onChange={(e) => {
                  setCategorySearch(e.target.value);
                  setShowCategoryDropdown(true);
                  // Clear formData.category if user is editing
                  if (formData.category && e.target.value !== formData.category) {
                    setFormData({ ...formData, category: '' });
                  }
                }}
                onFocus={() => setShowCategoryDropdown(true)}
                onBlur={() => {
                  // Delay so click on dropdown registers first
                  setTimeout(() => setShowCategoryDropdown(false), 200);
                }}
                data-testid="expense-category-input"
                autoComplete="off"
              />
              {categorySearch && (
                <button
                  type="button"
                  onClick={() => {
                    setCategorySearch('');
                    setFormData({ ...formData, category: '' });
                    if (categoryInputRef.current) categoryInputRef.current.focus();
                  }}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: '1rem',
                    padding: '0 4px'
                  }}
                  aria-label="Clear category"
                >
                  ✕
                </button>
              )}
              {showCategoryDropdown && filteredCategories.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  maxHeight: '240px',
                  overflowY: 'auto',
                  zIndex: 50,
                  marginTop: '4px'
                }}>
                  {filteredCategories.map((cat) => (
                    <div
                      key={cat}
                      onMouseDown={(e) => { e.preventDefault(); handleCategorySelect(cat); }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: formData.category === cat ? '#eff6ff' : 'transparent',
                        fontSize: '0.875rem'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = formData.category === cat ? '#eff6ff' : 'transparent'}
                      data-testid={`category-option-${cat}`}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
              )}
              {showCategoryDropdown && categorySearch && filteredCategories.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '12px',
                  zIndex: 50,
                  marginTop: '4px',
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem'
                }}>
                  <span>No categories matching "<strong>{categorySearch}</strong>".</span>
                  {/* Feb 2026 — inline add on the fly. onMouseDown fires
                      before onBlur closes the dropdown, so the click actually
                      registers. Persists to settings.expense_categories +
                      expense_categories_with_codes (same shape as the
                      standalone Add Category dialog). */}
                  <button
                    type="button"
                    data-testid="expense-category-inline-add"
                    onMouseDown={async (e) => {
                      e.preventDefault();
                      const val = categorySearch.trim();
                      if (!val) return;
                      try {
                        const currentCategories = settings?.expense_categories || [];
                        const updatedCategories = [...currentCategories, val];
                        const currentWithCodes = settings?.expense_categories_with_codes || [];
                        const updatedWithCodes = [...currentWithCodes, { name: val, code: '' }];
                        await axios.put(`${API}/settings`, {
                          expense_categories: updatedCategories,
                          expense_categories_with_codes: updatedWithCodes,
                        });
                        const settingsRes = await axios.get(`${API}/settings`);
                        setSettings(settingsRes.data);
                        handleCategorySelect(val);
                        toast.success(`Added "${val}" to categories`);
                      } catch (err) {
                        toast.error(`Couldn't save: ${err?.response?.data?.detail || err.message}`);
                      }
                    }}
                    style={{
                      background: '#16a34a',
                      color: '#fff',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    title={`Save "${categorySearch}" to your expense categories`}
                  >
                    + Add {categorySearch.length > 15 ? categorySearch.slice(0, 15) + '…' : categorySearch}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Row 3: Description — Aug 2026 (v3 — simple + stable).
              A "Recent + Common" dropdown next to a free-text input. The
              dropdown includes your last 20 typed descriptions (purple pill)
              plus the built-in common list, deduped. Typing in the free-text
              input works normally (uncontrolled, no re-render per key). */}
          <div>
            <Label>Description *</Label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Select
                value=""
                onValueChange={(value) => {
                  const currentValues = {
                    amount_ex_gst: localAmountRef.current?.value || formData.amount_ex_gst,
                    gst_amount: localGstRef.current?.value || formData.gst_amount,
                    total_amount: localTotalRef.current?.value || formData.total_amount,
                    reference: localRefRef.current?.value || formData.reference,
                    notes: localNotesRef.current?.value || formData.notes,
                  };
                  if (localDescRef.current) localDescRef.current.value = value;
                  setFormData({ ...formData, ...currentValues, description: value });
                }}
              >
                <SelectTrigger style={{ width: '200px', flexShrink: 0 }} data-testid="expense-description-preset">
                  <SelectValue placeholder="Recent / Common..." />
                </SelectTrigger>
                <SelectContent>
                  {recentDescriptions.length > 0 && (
                    <>
                      <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.7rem', color: '#7c3aed', fontWeight: 600, textTransform: 'uppercase' }}>Recent</div>
                      {recentDescriptions.map((desc) => (
                        <SelectItem key={`recent-${desc}`} value={desc}>{desc}</SelectItem>
                      ))}
                      <div style={{ borderTop: '1px solid #e2e8f0', margin: '0.25rem 0' }} />
                      <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Common</div>
                    </>
                  )}
                  {commonDescriptions.filter(d => !recentDescriptions.includes(d)).map((desc) => (
                    <SelectItem key={desc} value={desc}>{desc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                ref={localDescRef}
                defaultValue={formData.description}
                placeholder="Or type description..."
                data-testid="expense-description-input"
                required
                style={{ flex: 1 }}
              />
            </div>
          </div>

          {/* Row 4: Amounts */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <div>
              <Label>Amount (ex GST)</Label>
              <Input
                ref={localAmountRef}
                type="number"
                step="0.01"
                defaultValue={formData.amount_ex_gst}
                onChange={(e) => handleExGstChange(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Label>GST {gstFree ? '(GST-Free)' : '(10%)'}</Label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', cursor: 'pointer', color: '#475569' }}>
                  <input
                    type="checkbox"
                    checked={gstFree}
                    onChange={(e) => handleToggleGstFree(e.target.checked)}
                    data-testid="expense-gst-free-toggle"
                  />
                  GST-Free
                </label>
              </div>
              <Input
                ref={localGstRef}
                type="number"
                step="0.01"
                defaultValue={formData.gst_amount}
                placeholder="0.00"
                disabled={gstFree}
              />
            </div>
            <div>
              <Label>Total Amount *</Label>
              <Input
                ref={localTotalRef}
                type="number"
                step="0.01"
                defaultValue={formData.total_amount}
                onChange={(e) => handleTotalChange(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* Feb 2026 — Bulk transaction lines. Only shown on Add (not Edit).
              Each line becomes an independent Expense document on submit.
              Perfect for repeating charges like Google Ads or owner
              drawings — enter once with the shared supplier/category/notes,
              then hit "+ Add another transaction line" for every extra
              date+amount you need. */}
          {allowBulk && (
            <div data-testid="bulk-lines-section" style={{ padding: '0.85rem 1rem', border: '1px dashed #cbd5e1', borderRadius: '0.5rem', backgroundColor: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: extraLines.length > 0 ? '0.75rem' : 0 }}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Additional transaction lines</div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                    For recurring charges (Google Ads, owner drawings…). Each line becomes a separate expense entry with its own date.
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addExtraLine}
                  data-testid="bulk-add-line-btn"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  ➕ Add another line
                </Button>
              </div>

              {extraLines.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {extraLines.map((line, i) => (
                    <div
                      key={line.id}
                      data-testid={`bulk-line-row-${i}`}
                      style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr 1fr auto', gap: '0.5rem', alignItems: 'end', padding: '0.5rem 0.65rem', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.375rem' }}
                    >
                      <div>
                        <Label style={{ fontSize: '0.7rem' }}>Date · Line {i + 2}</Label>
                        <Input
                          type="date"
                          value={line.date}
                          onChange={(e) => patchExtraLine(line.id, { date: e.target.value })}
                          data-testid={`bulk-line-date-${i}`}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <Label style={{ fontSize: '0.7rem' }}>Amount (ex GST)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={line.amount_ex_gst}
                          placeholder="0.00"
                          onChange={(e) => recalcLineFromEx(line.id, e.target.value)}
                          data-testid={`bulk-line-ex-${i}`}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label style={{ fontSize: '0.7rem' }}>GST {line.gst_free ? '(free)' : '(10%)'}</Label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.65rem', cursor: 'pointer', color: '#475569' }}>
                            <input
                              type="checkbox"
                              checked={line.gst_free}
                              onChange={(e) => toggleLineGstFree(line.id, e.target.checked)}
                              data-testid={`bulk-line-gst-free-${i}`}
                            />
                            Free
                          </label>
                        </div>
                        <Input
                          type="number"
                          step="0.01"
                          value={line.gst_amount}
                          placeholder="0.00"
                          disabled={line.gst_free}
                          onChange={(e) => patchExtraLine(line.id, { gst_amount: e.target.value })}
                          data-testid={`bulk-line-gst-${i}`}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <Label style={{ fontSize: '0.7rem' }}>Total *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={line.total_amount}
                          placeholder="0.00"
                          onChange={(e) => recalcLineFromTotal(line.id, e.target.value)}
                          data-testid={`bulk-line-total-${i}`}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeExtraLine(line.id)}
                        data-testid={`bulk-line-remove-${i}`}
                        title="Remove this line"
                        style={{ color: '#dc2626', padding: '0.25rem 0.5rem' }}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                  {extraLines.length > 0 && (
                    <div data-testid="bulk-lines-summary" style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: '#0f766e', backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '0.375rem', textAlign: 'right' }}>
                      {extraLines.length + 1} transactions ·&nbsp;total ${(
                        (parseFloat(
                          // Prefer the live DOM value (main Total is an
                          // uncontrolled input) then fall back to formData.
                          localTotalRef.current?.value ?? formData.total_amount
                        ) || 0) +
                        extraLines.reduce((s, l) => s + (parseFloat(l.total_amount) || 0), 0)
                      ).toFixed(2)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Row 5: Payment Method and Reference */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <Label>Payment Method</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(value) => {
                  // Capture current ref values before state update
                  const currentValues = {
                    description: localDescRef.current?.value || formData.description,
                    amount_ex_gst: localAmountRef.current?.value || formData.amount_ex_gst,
                    gst_amount: localGstRef.current?.value || formData.gst_amount,
                    total_amount: localTotalRef.current?.value || formData.total_amount,
                    reference: localRefRef.current?.value || formData.reference,
                    notes: localNotesRef.current?.value || formData.notes
                  };
                  setFormData({ ...formData, ...currentValues, payment_method: value });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((method) => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice/Reference Number</Label>
              <Input
                ref={localRefRef}
                defaultValue={formData.reference}
                placeholder="e.g., INV-001234"
              />
            </div>
          </div>

          {/* Row 6: Notes */}
          <div>
            <Label>Notes</Label>
            <Textarea
              ref={localNotesRef}
              defaultValue={formData.notes}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          {/* Row 6b: Category type (Feb 2026) — 3-way selector replaces the
              old is_internal switch. See Purchase form for full rationale. */}
          {(() => {
            const bucket = isInternal
              ? 'personal_not_claimed'
              : (isPersonalUseClaimed ? 'personal_claimed' : 'business');
            const bg = bucket === 'personal_not_claimed' ? '#fef3c7'
                      : bucket === 'personal_claimed'    ? '#fef2f2'
                      : '#f8fafc';
            const bd = bucket === 'personal_not_claimed' ? '#fcd34d'
                      : bucket === 'personal_claimed'    ? '#fca5a5'
                      : '#e2e8f0';
            return (
          <div style={{ padding: '0.75rem 1rem', backgroundColor: bg, border: '1px solid', borderColor: bd, borderRadius: '0.5rem' }}>
            <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem' }}>Category type</div>
            <select
              value={bucket}
              onChange={(e) => {
                const v = e.target.value;
                setIsInternal(v === 'personal_not_claimed');
                setIsPersonalUseClaimed(v === 'personal_claimed');
              }}
              data-testid="expense-bucket-select"
              style={{ padding: '0.4rem 0.6rem', borderRadius: '0.375rem', border: '1px solid #cbd5e1', fontWeight: 600, width: '100%', marginBottom: '0.35rem' }}
            >
              <option value="business">Business (default) — normal deductible expense</option>
              <option value="personal_not_claimed">Personal — Not Claimed (excluded from P&amp;L)</option>
              <option value="personal_claimed">Personal — Claimed (kept in P&amp;L, flagged as personal-use)</option>
            </select>
            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
              {bucket === 'business' && 'Fully deductible business expense. Appears in your standard P&L.'}
              {bucket === 'personal_not_claimed' && 'Paid from business account but not for business use. EXCLUDED from P&L — not deducted for tax.'}
              {bucket === 'personal_claimed' && 'Kept in P&L (deductible) but flagged so you can see personal-use claims separately at EOFY.'}
            </div>
          </div>
            );
          })()}

          {/* Row 6c: Booked / Finished status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', backgroundColor: expenseStatus === 'Booked' ? '#e0e7ff' : '#f8fafc', border: '1px solid', borderColor: expenseStatus === 'Booked' ? '#818cf8' : '#e2e8f0', borderRadius: '0.5rem' }}>
            <select
              value={expenseStatus}
              onChange={(e) => setExpenseStatus(e.target.value)}
              data-testid="expense-status-select"
              style={{ padding: '0.4rem 0.6rem', borderRadius: '0.375rem', border: '1px solid #cbd5e1', fontWeight: 600 }}
            >
              <option value="Finished">Finished</option>
              <option value="Booked">Booked (provisional)</option>
            </select>
            <div style={{ flex: 1, fontSize: '0.75rem', color: '#64748b' }}>
              {expenseStatus === 'Booked'
                ? 'Provisional — this expense is NOT counted in P&L until you change status to Finished. Use for prepaid shipping labels, pre-orders etc.'
                : 'This expense is fully accounted for and will appear in P&L reports.'}
            </div>
          </div>

          {/* Row 7: Receipt Upload */}
          <div>
            <Label>Receipt / Invoice Image</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setReceiptFile(e.target.files[0])}
                style={{ flex: 1 }}
                data-testid="receipt-upload-input"
              />
              {receiptFile && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setReceiptFile(null)}
                >
                  Clear
                </Button>
              )}
            </div>
            {receiptFile && (
              <div className="text-xs text-muted-foreground mt-1">
                Selected: {receiptFile.name} ({(receiptFile.size / 1024).toFixed(1)} KB)
              </div>
            )}
            {selectedExpense?.receipt_url && !receiptFile && (
              <div className="text-xs text-green-600 mt-1">
                📎 Receipt already attached - <a href={buildReceiptViewUrl(selectedExpense.receipt_url)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>View</a>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
          <Button type="button" variant="outline" onClick={() => {
            setShowAddDialog(false);
            setShowEditDialog(false);
            setReceiptFile(null);
          }}>
            Cancel
          </Button>
          <Button type="submit" disabled={uploadingReceipt}>
            {uploadingReceipt ? 'Uploading...' : submitLabel}
          </Button>
        </div>
      </form>
    );
    };

    return (
      <div className="page-content" data-testid="expenses-page">
        <div className="page-header">
          <h1>Business Expenses</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              type="button"
              onClick={() => {
                const totalExpenses = expenses.reduce((sum, e) => sum + (e.total_amount || 0), 0);
                const totalGST = expenses.reduce((sum, e) => sum + (e.gst_amount || 0), 0);
                const totalExGST = expenses.reduce((sum, e) => sum + (e.amount_ex_gst || 0), 0);
                const filterLabel = internalFilter === 'all' ? 'All Expenses' : (internalFilter === 'internal' ? 'Internal / Personal Only' : 'Business Only');
                
                const contentHtml = `
                  <div style="margin-bottom: 12px; padding: 8px 12px; background: #f1f5f9; border-radius: 6px; font-size: 12px; color: #334155;">
                    <strong>Filter:</strong> ${filterLabel}
                  </div>
                  <div class="summary-grid">
                    <div class="summary-card red">
                      <div class="summary-label">Total Expenses</div>
                      <div class="summary-value red">$${totalExpenses.toFixed(2)}</div>
                    </div>
                    <div class="summary-card green">
                      <div class="summary-label">GST Claimable</div>
                      <div class="summary-value green">$${totalGST.toFixed(2)}</div>
                    </div>
                  </div>
                  
                  <div class="section-title">Expense Details (${expenses.length} records)</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Supplier</th>
                        <th class="text-right">Ex GST</th>
                        <th class="text-right">GST</th>
                        <th class="text-right">Total</th>
                        <th>Internal</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${expenses.map(exp => `
                        <tr${exp.is_internal ? ' style="background:#fffbeb;"' : ''}>
                          <td>${exp.date ? new Date(exp.date).toLocaleDateString('en-AU') : '-'}</td>
                          <td>${exp.description || ''}</td>
                          <td>${exp.category || ''}</td>
                          <td>${exp.supplier_name || '-'}</td>
                          <td class="text-right">$${(exp.amount_ex_gst || 0).toFixed(2)}</td>
                          <td class="text-right">$${(exp.gst_amount || 0).toFixed(2)}</td>
                          <td class="text-right">$${(exp.total_amount || 0).toFixed(2)}</td>
                          <td>${exp.is_internal ? '<span style="color:#92400e;font-weight:600;">Yes</span>' : '-'}</td>
                        </tr>
                      `).join('')}
                      <tr class="totals-row">
                        <td colspan="4"><strong>TOTALS</strong></td>
                        <td class="text-right"><strong>$${totalExGST.toFixed(2)}</strong></td>
                        <td class="text-right"><strong>$${totalGST.toFixed(2)}</strong></td>
                        <td class="text-right"><strong>$${totalExpenses.toFixed(2)}</strong></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                `;
                openPrintPreview('Business Expenses Report', contentHtml, { printerHint: settings?.printer_job_card });
              }}
              data-testid="print-expenses"
              title="Open print preview"
              style={{ 
                backgroundColor: '#6366f1', 
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              🖨️ Print
            </button>
            <Button 
              variant="outline" 
              onClick={() => setShowAddCategoryDialog(true)} 
              data-testid="add-category-btn-expenses"
            >
              + Add Category
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowAddSupplierDialog(true)} 
              data-testid="add-supplier-btn-expenses"
            >
              + Add Supplier
            </Button>
            <Button onClick={handleAdd} data-testid="add-expense-btn">+ Add Expense</Button>
          </div>
        </div>

        {/* Quick Entry Section */}
        {settings?.expense_quick_templates && settings.expense_quick_templates.length > 0 && (
          <Card style={{ marginBottom: '1.5rem', backgroundColor: '#f8fafc' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">⚡ Quick Entry</CardTitle>
              <CardDescription>Click a template to quickly add a common expense</CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {settings.expense_quick_templates.map((template, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormData({
                        date: todayLocalISO(),
                        description: template.description,
                        category: template.category,
                        supplier_id: '',
                        supplier_name: '',
                        amount_ex_gst: '',
                        gst_amount: '',
                        total_amount: '',
                        payment_method: template.payment_method || settings?.default_payment_method || 'Bank Transfer',
                        reference: '',
                        notes: ''
                      });
                      setShowAddDialog(true);
                    }}
                    data-testid={`quick-entry-${idx}`}
                  >
                    {template.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Expenses</CardDescription>
              <CardTitle className="text-2xl" style={{ color: '#dc2626' }}>
                {formatCurrency(getTotalExpenses())}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>GST Claimed</CardDescription>
              <CardTitle className="text-2xl">
                {formatCurrency(getTotalGST())}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Expense Count</CardDescription>
              <CardTitle className="text-2xl">{expenses.length}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Payment Methods Breakdown — how you paid, for the currently-filtered
            expenses. Purely a read view over the `expenses` array in scope.
            Click any row to narrow the transactions table below to just that
            payment method — perfect for reconciling against a bank/CC statement. */}
        {paymentMethodBreakdown.length > 0 && (
          <Card style={{ marginBottom: '1.5rem' }} data-testid="expense-payment-method-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">💳 Payment Methods Breakdown</CardTitle>
              <CardDescription>
                How you paid these expenses (respects the filters below).
                Click a row to filter the transactions table by that method.
                {pmFilter && (
                  <span
                    onClick={(e) => { e.stopPropagation(); setPmFilter(null); }}
                    style={{
                      marginLeft: '0.5rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.1rem 0.5rem',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      fontWeight: 600,
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                    }}
                    data-testid="expense-pm-filter-chip"
                  >
                    Filtered: {pmFilter} ✕
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Method</TableHead>
                    <TableHead style={{ textAlign: 'right' }}># Expenses</TableHead>
                    <TableHead style={{ textAlign: 'right' }}>Subtotal (ex GST)</TableHead>
                    <TableHead style={{ textAlign: 'right' }}>Total (inc GST)</TableHead>
                    <TableHead style={{ textAlign: 'right', width: '80px' }}>% of total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentMethodBreakdown.map((row) => {
                    const isActive = pmFilter === row.method;
                    return (
                      <TableRow
                        key={row.method}
                        data-testid={`expense-pm-row-${row.method.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => setPmFilter(isActive ? null : row.method)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isActive ? '#dbeafe' : undefined,
                          fontWeight: isActive ? 600 : undefined,
                        }}
                        className="hover:bg-slate-50"
                      >
                        <TableCell style={{ fontWeight: 500 }}>{row.method}</TableCell>
                        <TableCell style={{ textAlign: 'right' }}>{row.count}</TableCell>
                        <TableCell style={{ textAlign: 'right' }}>{formatCurrency(row.subtotal)}</TableCell>
                        <TableCell style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(row.total)}</TableCell>
                        <TableCell style={{ textAlign: 'right', color: '#64748b' }}>{row.pct.toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>
                    <TableCell>Total</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{paymentMethodBreakdown.reduce((s, r) => s + r.count, 0)}</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{formatCurrency(paymentMethodBreakdown.reduce((s, r) => s + r.subtotal, 0))}</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{formatCurrency(paymentMethodBreakdown.reduce((s, r) => s + r.total, 0))}</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardHeader>
            <CardTitle className="text-lg">Filter Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
              <DateRangePicker
                dateRange={expDateRange}
                setDateRange={setExpDateRange}
                startDate={expCustomStart}
                setStartDate={setExpCustomStart}
                endDate={expCustomEnd}
                setEndDate={setExpCustomEnd}
                label="Date Range"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
                <div style={{ position: 'relative' }}>
                  <Label>Category</Label>
                  <Input
                    type="text"
                    placeholder="Start typing a category..."
                    value={filterCategorySearch}
                    onChange={(e) => {
                      setFilterCategorySearch(e.target.value);
                      setShowFilterCategoryDropdown(true);
                      if (!e.target.value) handleFilterChange('category', '');
                    }}
                    onFocus={() => setShowFilterCategoryDropdown(true)}
                    onBlur={() => setTimeout(() => setShowFilterCategoryDropdown(false), 200)}
                    data-testid="expense-filter-category-input"
                  />
                  {showFilterCategoryDropdown && (() => {
                    const q = filterCategorySearch.toLowerCase().trim();
                    const list = [...expenseCategories]
                      .sort((a, b) => (a || '').localeCompare(b || ''))
                      .filter(c => !q || (c || '').toLowerCase().includes(q));
                    return (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '260px', overflowY: 'auto', background: 'white', border: '1px solid #cbd5e1', borderRadius: '0.375rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, marginTop: '0.25rem' }}>
                        <div
                          onClick={() => { handleFilterChange('category', ''); setFilterCategorySearch(''); setShowFilterCategoryDropdown(false); }}
                          onMouseDown={(e) => e.preventDefault()}
                          style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #e2e8f0', fontStyle: 'italic', color: '#475569' }}
                          data-testid="expense-filter-category-option-all"
                        >
                          All categories
                        </div>
                        {list.slice(0, 100).map((cat) => (
                          <div
                            key={cat}
                            onClick={() => { handleFilterChange('category', cat); setFilterCategorySearch(cat); setShowFilterCategoryDropdown(false); }}
                            onMouseDown={(e) => e.preventDefault()}
                            style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', backgroundColor: filters.category === cat ? '#eff6ff' : 'transparent' }}
                            data-testid={`expense-filter-category-option-${cat}`}
                          >
                            {cat}
                          </div>
                        ))}
                        {list.length === 0 && (
                          <div style={{ padding: '0.5rem 0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>No matching categories</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div style={{ position: 'relative' }}>
                  <Label>Supplier</Label>
                  <Input
                    type="text"
                    placeholder="Start typing a supplier name..."
                    value={filterSupplierSearch}
                    onChange={(e) => {
                      setFilterSupplierSearch(e.target.value);
                      setShowFilterSupplierDropdown(true);
                      if (!e.target.value) handleFilterChange('supplier_id', '');
                    }}
                    onFocus={() => setShowFilterSupplierDropdown(true)}
                    onBlur={() => setTimeout(() => setShowFilterSupplierDropdown(false), 200)}
                    data-testid="expense-filter-supplier-input"
                  />
                  {showFilterSupplierDropdown && (() => {
                    const q = filterSupplierSearch.toLowerCase().trim();
                    const list = [...suppliers]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .filter(s => !q || (s.name || '').toLowerCase().includes(q));
                    return (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '260px', overflowY: 'auto', background: 'white', border: '1px solid #cbd5e1', borderRadius: '0.375rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, marginTop: '0.25rem' }}>
                        <div
                          onClick={() => { handleFilterChange('supplier_id', ''); setFilterSupplierSearch(''); setShowFilterSupplierDropdown(false); }}
                          onMouseDown={(e) => e.preventDefault()}
                          style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #e2e8f0', fontStyle: 'italic', color: '#475569' }}
                          data-testid="expense-filter-supplier-option-all"
                        >
                          All suppliers
                        </div>
                        {list.slice(0, 100).map((s) => (
                          <div
                            key={s.id}
                            onClick={() => { handleFilterChange('supplier_id', s.id); setFilterSupplierSearch(s.name); setShowFilterSupplierDropdown(false); }}
                            onMouseDown={(e) => e.preventDefault()}
                            style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', backgroundColor: filters.supplier_id === s.id ? '#eff6ff' : 'transparent' }}
                            data-testid={`expense-filter-supplier-option-${s.id}`}
                          >
                            {s.name}
                          </div>
                        ))}
                        {list.length === 0 && (
                          <div style={{ padding: '0.5rem 0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>No matching suppliers</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <Button variant="outline" onClick={handleClearFilters} data-testid="clear-filters-btn">Clear All</Button>
                </div>
              </div>

              {/* Internal vs Business segmented filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <Label style={{ marginBottom: 0 }}>Show:</Label>
                {[
                  { value: 'all', label: 'All Expenses' },
                  { value: 'business', label: 'Business Only' },
                  { value: 'internal', label: 'Internal / Personal Only' },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={internalFilter === opt.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setInternalFilter(opt.value)}
                    data-testid={`expense-internal-filter-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
                {/* Reconciliation filter — hides rows already ticked so the
                    user can focus on remaining reconciliation work. */}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Button
                    type="button"
                    variant={unreconciledOnly ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUnreconciledOnly(!unreconciledOnly)}
                    data-testid="expense-unreconciled-filter"
                  >
                    {unreconciledOnly ? '☑ Showing unreconciled only' : '☐ Show unreconciled only'}
                  </Button>
                  {expenses.length > 0 && (() => {
                    const reconciled = expenses.filter(e => e.reconciled).length;
                    const total = expenses.length;
                    return (
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {reconciled} / {total} reconciled ({total > 0 ? Math.round(reconciled * 100 / total) : 0}%)
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: '60px', textAlign: 'center' }} title="Tick when reconciled against your statement">☑</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Amount (ex GST)</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Receipt</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                      {unreconciledOnly && expenses.length > 0
                        ? `All ${expenses.length} matching expense(s) reconciled ✓ — untick "Show unreconciled only" to see them again.`
                        : pmFilter
                          ? `No expenses paid via ${pmFilter} in the current filters. Click the highlighted row in Payment Methods Breakdown to clear.`
                          : `No expenses found. Click "Add Expense" to record your first expense.`}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleExpenses.map((expense) => (
                    <TableRow
                      key={expense.id}
                      data-testid={`expense-row-${expense.id}`}
                      style={{
                        ...(expense.reconciled
                          ? { backgroundColor: '#f0fdf4', opacity: 0.85 }
                          : (expense.is_internal ? { backgroundColor: '#fffbeb' } : {})),
                        ...(highlightExpenseId === expense.id
                          ? { backgroundColor: '#fef3c7', outline: '2px solid #f59e0b', transition: 'background-color 300ms ease' }
                          : {})
                      }}
                    >
                      <TableCell style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!expense.reconciled}
                          onChange={() => toggleReconciled(expense.id, expense.reconciled)}
                          data-testid={`expense-reconciled-${expense.id}`}
                          title={expense.reconciled_at ? `Reconciled ${new Date(expense.reconciled_at).toLocaleDateString()} by ${expense.reconciled_by || 'unknown'}` : 'Tick when matched against your statement'}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#059669' }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(expense.date)}</TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <span>{expense.description}</span>
                          {expense.is_internal && (
                            <span title="Internal / Personal — excluded from business P&L" style={{ fontSize: '0.65rem', fontWeight: 700, color: '#92400e', backgroundColor: '#fde68a', padding: '0.1rem 0.4rem', borderRadius: '999px', letterSpacing: '0.03em' }} data-testid={`expense-internal-badge-${expense.id}`}>
                              INTERNAL
                            </span>
                          )}
                          {expense.personal_use_claimed && !expense.is_internal && (
                            <span title="Personal-use — CLAIMED as deductible (kept in P&L, flagged for owner tracking)" style={{ fontSize: '0.65rem', fontWeight: 700, color: '#991b1b', backgroundColor: '#fecaca', padding: '0.1rem 0.4rem', borderRadius: '999px', letterSpacing: '0.03em' }} data-testid={`expense-personal-claimed-badge-${expense.id}`}>
                              PERSONAL · CLAIMED
                            </span>
                          )}
                        </div>
                        {expense.reference && (
                          <div className="text-xs text-muted-foreground">Ref: {expense.reference}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-100">
                          {expense.category}
                        </span>
                      </TableCell>
                      <TableCell>{expense.supplier_name || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(expense.amount_ex_gst)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(expense.gst_amount)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(expense.total_amount)}</TableCell>
                      <TableCell>{expense.payment_method}</TableCell>
                      <TableCell>
                        {expense.receipt_url ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <a 
                              href={buildReceiptViewUrl(expense.receipt_url)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                              data-testid={`view-receipt-${expense.id}`}
                            >
                              📎 View
                            </a>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 p-1 h-auto"
                              onClick={() => handleDeleteReceipt(expense.id)}
                              title="Delete receipt"
                            >
                              ✕
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const isBooked = (expense.status || 'Finished') === 'Booked';
                          return (
                            <span
                              data-testid={`expense-status-badge-${expense.id}`}
                              style={{
                                display: 'inline-block',
                                padding: '0.15rem 0.55rem',
                                borderRadius: '999px',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                                color: isBooked ? '#92400e' : '#065f46',
                                backgroundColor: isBooked ? '#fef3c7' : '#d1fae5',
                                border: `1px solid ${isBooked ? '#fcd34d' : '#6ee7b7'}`
                              }}
                              title={isBooked ? 'Provisional — money out, not yet realised on P&L' : 'Realised expense — included in P&L'}
                            >
                              {isBooked ? 'BOOKED' : 'FINISHED'}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(expense)}
                          data-testid={`edit-expense-${expense.id}`}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(expense)}
                          data-testid={`copy-expense-${expense.id}`}
                          title="Create a new expense pre-filled from this one — date and amount will be blank for you to enter"
                        >
                          📋 Copy
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteClick(expense)}
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add Expense Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Add New Expense</DialogTitle>
              <DialogDescription>Record a new business expense</DialogDescription>
            </DialogHeader>
            <ExpenseForm key="add-form" onSubmit={handleSubmitAdd} submitLabel="Add Expense" allowBulk={true} />
          </DialogContent>
        </Dialog>

        {/* Edit Expense Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Edit Expense</DialogTitle>
              <DialogDescription>Update expense details</DialogDescription>
            </DialogHeader>
            <ExpenseForm key={`edit-${selectedExpense?.id || 'new'}`} onSubmit={handleSubmitEdit} submitLabel="Update Expense" />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Expense</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this expense? This action cannot be undone.
                {expenseToDelete && (
                  <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f1f5f9', borderRadius: '4px' }}>
                    <strong>{expenseToDelete.description}</strong> - {formatCurrency(expenseToDelete.total_amount)}
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add Supplier Dialog */}
        <Dialog open={showAddSupplierDialog} onOpenChange={setShowAddSupplierDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Supplier</DialogTitle>
              <DialogDescription>Add a supplier to link with your expenses</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddSupplier}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <Label>Supplier Name *</Label>
                  <Input
                    value={supplierFormData.name}
                    onChange={(e) => setSupplierFormData({ ...supplierFormData, name: e.target.value })}
                    placeholder="e.g., Power Company Ltd"
                    required
                    data-testid="supplier-name-input"
                  />
                </div>
                <div>
                  <Label>Contact Person</Label>
                  <Input
                    value={supplierFormData.contact_person}
                    onChange={(e) => setSupplierFormData({ ...supplierFormData, contact_person: e.target.value })}
                    placeholder="e.g., John Smith"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={supplierFormData.email}
                      onChange={(e) => setSupplierFormData({ ...supplierFormData, email: e.target.value })}
                      placeholder="billing@supplier.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={supplierFormData.phone}
                      onChange={(e) => setSupplierFormData({ ...supplierFormData, phone: e.target.value })}
                      placeholder="(02) 1234 5678"
                    />
                  </div>
                </div>
                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={supplierFormData.address}
                    onChange={(e) => setSupplierFormData({ ...supplierFormData, address: e.target.value })}
                    placeholder="123 Business St, Sydney NSW 2000"
                    rows={2}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => setShowAddSupplierDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-supplier-btn">Add Supplier</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Category Dialog */}
        <Dialog open={showAddCategoryDialog} onOpenChange={setShowAddCategoryDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add New Category</DialogTitle>
              <DialogDescription>Add a new expense category</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddCategory}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <Label>Category Name *</Label>
                  <Input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="e.g., Office Supplies"
                    required
                    data-testid="new-category-input"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => setShowAddCategoryDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-category-btn">Add Category</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  return (
    <AuthContext.Provider value={{ user, login, verify2FA, send2FAOtp, logout, loading }}>
      <div className="App">
        {/* Auto-logout warning banner */}
        {autoLogoutWarning && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: '#fef3c7',
            borderBottom: '2px solid #f59e0b',
            padding: '0.75rem 1rem',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.25rem' }}>⚠️</span>
            <span style={{ fontWeight: '600', color: '#92400e' }}>
              Auto-logout in {autoLogoutWarning.minutes} minute{autoLogoutWarning.minutes !== 1 ? 's' : ''} 
              (at {autoLogoutWarning.time})
            </span>
            <span style={{ color: '#92400e', fontSize: '0.875rem' }}>
              Save your work now!
            </span>
          </div>
        )}
        {user && <GlobalSearch />}
        
        {/* Mobile Navigation Overlay and Panel */}
        {user && (
          <>
            <div 
              className={`mobile-nav-overlay ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className={`mobile-nav-panel ${mobileMenuOpen ? 'open' : ''}`}>
              <div className="mobile-nav-header">
                <h2>Total Data Recall</h2>
                <button 
                  className="mobile-nav-close" 
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                >
                  ✕
                </button>
              </div>
              <div className="mobile-nav-links">
                <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                  <span>📊</span> Dashboard
                </Link>
                <Link to="/new" onClick={() => setMobileMenuOpen(false)}>
                  <span>➕</span> Add Job
                </Link>
                <Link to="/jobs" onClick={() => setMobileMenuOpen(false)}>
                  <span>📋</span> All Jobs
                </Link>
                <Link to="/reminders" onClick={() => setMobileMenuOpen(false)}>
                  <span>🔔</span> Reminders
                </Link>
                <Link to="/customers" onClick={() => setMobileMenuOpen(false)}>
                  <span>👥</span> Customers
                </Link>
                <Link to="/suppliers" onClick={() => setMobileMenuOpen(false)}>
                  <span>🏭</span> Suppliers
                </Link>
                <Link to="/stock" onClick={() => setMobileMenuOpen(false)}>
                  <span>📦</span> Stock
                </Link>
                <Link to="/purchases" onClick={() => setMobileMenuOpen(false)}>
                  <span>🛒</span> Purchases
                </Link>
                <Link to="/expenses" onClick={() => setMobileMenuOpen(false)}>
                  <span>💰</span> Expenses
                </Link>
                {user.role === 'admin' && (
                  <Link to="/reports" onClick={() => setMobileMenuOpen(false)}>
                    <span>📈</span> Reports
                  </Link>
                )}
                <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>
                  <span>⚙️</span> Settings
                </Link>
                <Link to="/help" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-help">
                  <span>📖</span> Help
                </Link>
              </div>
              <div className="mobile-user-section">
                <div className="mobile-user-info">
                  <div className="mobile-user-avatar">
                    {user.username?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div style={{ fontWeight: '600' }}>{user.username}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{user.role}</div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  style={{ width: '100%' }}
                >
                  Logout
                </Button>
              </div>
            </div>
          </>
        )}
        
        <Routes>
          {/* Public Routes (No Auth Required) */}
          <Route path="/sign/:token" element={<PublicSignaturePage />} />
          <Route path="/quote-approval/:token" element={<QuoteApprovalPage />} />
          <Route path="/kiosk" element={<KioskPage />} />
          
          {/* Auth Routes */}
          {!user ? (
            <>
              <Route path="/login" element={<LoginPage />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <>
              <Route path="/" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button 
                      className="mobile-menu-toggle" 
                      onClick={() => setMobileMenuOpen(true)}
                      aria-label="Open menu"
                    >
                      ☰
                    </button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <Dashboard />
                  </main>
                </div>
              } />
              <Route path="/new" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <NewJobForm />
                  </main>
                </div>
              } />
              <Route path="/job/:jobId" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <JobDetail />
                  </main>
                </div>
              } />
              <Route path="/jobs" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <AllJobsPage />
                  </main>
                </div>
              } />
              <Route path="/reminders" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <RemindersPage />
                  </main>
                </div>
              } />
              <Route path="/customers" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <CustomersPage />
                  </main>
                </div>
              } />
              <Route path="/stock" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <StockPage />
                  </main>
                </div>
              } />
              <Route path="/purchases" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <PurchasesPage />
                  </main>
                </div>
              } />
              {user.role === 'admin' && (
                <Route path="/reports" element={
                  <div className="app-layout">
                    <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                      <div className="nav-brand">
                        <h1>Total Data Recall</h1>
                      </div>
                      <div className="nav-links">
                        <Link to="/" data-testid="nav-dashboard">
                          <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                        </Link>
                        <Link to="/new" data-testid="nav-new-job">
                          <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                        </Link>
                        <Link to="/jobs" data-testid="nav-all-jobs">
                          <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                        </Link>
                        <Link to="/reminders" data-testid="nav-reminders">
                          <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                        </Link>
                        <Link to="/customers" data-testid="nav-customers">
                          <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                        </Link>
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                        <Link to="/settings" data-testid="nav-settings">
                          <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                        </Link>
                        <div className="nav-user">
                          <span className="user-name">{user.full_name}</span>
                          <Button variant="outline" size="sm" onClick={logout}>
                            Logout
                          </Button>
                        </div>
                      </div>
                    </nav>

                    <main className="app-content">
                      <ReportsPage />
                    </main>
                  </div>
                } />
              )}
              <Route path="/suppliers" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <SuppliersPage />
                  </main>
                </div>
              } />
              <Route path="/expenses" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <ExpensesPage />
                  </main>
                </div>
              } />
              <Route path="/settings" element={
                <div className="app-layout">
                  <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                    <div className="nav-brand">
                      <h1>Total Data Recall</h1>
                    </div>
                    <div className="nav-links">
                      <Link to="/" data-testid="nav-dashboard">
                        <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                      </Link>
                      <Link to="/new" data-testid="nav-new-job">
                        <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                      </Link>
                      <Link to="/jobs" data-testid="nav-all-jobs">
                        <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                      </Link>
                      <Link to="/reminders" data-testid="nav-reminders">
                        <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                      </Link>
                      <Link to="/customers" data-testid="nav-customers">
                        <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                      </Link>
                      <Link to="/suppliers" data-testid="nav-suppliers">
                        <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                      </Link>
                      <Link to="/stock" data-testid="nav-stock">
                        <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                      </Link>
                      <Link to="/purchases" data-testid="nav-purchases">
                        <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                      </Link>
                      <Link to="/expenses" data-testid="nav-expenses">
                        <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                      )}
                      <Link to="/settings" data-testid="nav-settings">
                        <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                      </Link>
                      <Link to="/help" data-testid="nav-help">
                        <Button variant={isNavActive('/help') ? 'default' : 'ghost'} className={isNavActive('/help') ? 'nav-active' : ''}>Help</Button>
                      </Link>
                      <div className="nav-user">
                        <span className="user-name">{user.full_name}</span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                              Logout All
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out');
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      localStorage.removeItem('user');
                                      window.location.href = '/';
                                    }, 1000);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </div>
                  </nav>

                  <main className="app-content">
                    <SettingsPage />
                  </main>
                </div>
              } />
              {user.role === 'admin' && (
                <Route path="/newsletter" element={
                  <div className="app-layout">
                    <nav className="app-nav">
                    <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                      <div className="nav-brand">
                        <h1>Total Data Recall</h1>
                      </div>
                      <div className="nav-links">
                        <Link to="/" data-testid="nav-dashboard">
                          <Button variant={isNavActive('/') ? 'default' : 'ghost'} className={isNavActive('/') ? 'nav-active' : ''}>Dashboard</Button>
                        </Link>
                        <Link to="/new" data-testid="nav-new-job">
                          <Button variant={isNavActive('/new') ? 'default' : 'ghost'} className={isNavActive('/new') ? 'nav-active' : ''}>Add Job</Button>
                        </Link>
                        <Link to="/jobs" data-testid="nav-all-jobs">
                          <Button variant={isNavActive('/jobs') ? 'default' : 'ghost'} className={isNavActive('/jobs') ? 'nav-active' : ''}>All Jobs</Button>
                        </Link>
                        <Link to="/reminders" data-testid="nav-reminders">
                          <Button variant={isNavActive('/reminders') ? 'default' : 'ghost'} className={isNavActive('/reminders') ? 'nav-active' : ''}>Reminders</Button>
                        </Link>
                        <Link to="/customers" data-testid="nav-customers">
                          <Button variant={isNavActive('/customers') ? 'default' : 'ghost'} className={isNavActive('/customers') ? 'nav-active' : ''}>Customers</Button>
                        </Link>
                        <Link to="/suppliers" data-testid="nav-suppliers">
                          <Button variant={isNavActive('/suppliers') ? 'default' : 'ghost'} className={isNavActive('/suppliers') ? 'nav-active' : ''}>Suppliers</Button>
                        </Link>
                        <Link to="/stock" data-testid="nav-stock">
                          <Button variant={isNavActive('/stock') ? 'default' : 'ghost'} className={isNavActive('/stock') ? 'nav-active' : ''}>Stock</Button>
                        </Link>
                        <Link to="/purchases" data-testid="nav-purchases">
                          <Button variant={isNavActive('/purchases') ? 'default' : 'ghost'} className={isNavActive('/purchases') ? 'nav-active' : ''}>Purchases</Button>
                        </Link>
                        <Link to="/expenses" data-testid="nav-expenses">
                          <Button variant={isNavActive('/expenses') ? 'default' : 'ghost'} className={isNavActive('/expenses') ? 'nav-active' : ''}>Expenses</Button>
                        </Link>
                        <Link to="/reports" data-testid="nav-reports">
                          <Button variant={isNavActive('/reports') ? 'default' : 'ghost'} className={isNavActive('/reports') ? 'nav-active' : ''}>Reports</Button>
                        </Link>
                        <Link to="/newsletter" data-testid="nav-newsletter">
                          <Button variant={isNavActive('/newsletter') ? 'default' : 'ghost'} className={isNavActive('/newsletter') ? 'nav-active' : ''}>Newsletter</Button>
                        </Link>
                        <Link to="/settings" data-testid="nav-settings">
                          <Button variant={isNavActive('/settings') ? 'default' : 'ghost'} className={isNavActive('/settings') ? 'nav-active' : ''}>Settings</Button>
                        </Link>
                        <div className="nav-user">
                          <span className="user-name">{user.full_name}</span>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                                Logout All
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will immediately log out all workstations, including this one. 
                                  All users will need to log in again.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/auth/logout-all`);
                                      toast.success('All devices logged out');
                                      setTimeout(() => {
                                        localStorage.removeItem('token');
                                        localStorage.removeItem('user');
                                        window.location.href = '/';
                                      }, 1000);
                                    } catch (error) {
                                      toast.error('Failed to logout all devices');
                                    }
                                  }}
                                  style={{ backgroundColor: '#dc2626' }}
                                >
                                  Logout All
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <Button variant="outline" size="sm" onClick={logout}>
                            Logout
                          </Button>
                        </div>
                      </div>
                    </nav>

                    <main className="app-content">
                      <NewsletterPage />
                    </main>
                  </div>
                } />
              )}
              <Route path="/help" element={
                <ProtectedRoute>
                  <div className="app-layout">
                    <nav className="app-nav">
                      <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                      <div className="nav-brand">
                        <h1>Total Data Recall</h1>
                      </div>
                      <div className="nav-links">
                        <Link to="/" data-testid="nav-dashboard">
                          <Button variant="ghost">Dashboard</Button>
                        </Link>
                        <Link to="/help" data-testid="nav-help">
                          <Button variant="default">Help</Button>
                        </Link>
                      </div>
                      <div className="nav-user">
                        <span className="user-name">{user?.full_name}</span>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </nav>
                    <main className="app-content">
                      <HelpPage />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/help/changelog" element={
                <ProtectedRoute>
                  <div className="app-layout">
                    <nav className="app-nav">
                      <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">☰</button>
                      <div className="nav-brand">
                        <h1>Total Data Recall</h1>
                      </div>
                      <div className="nav-links">
                        <Link to="/" data-testid="nav-dashboard">
                          <Button variant="ghost">Dashboard</Button>
                        </Link>
                        <Link to="/help" data-testid="nav-help">
                          <Button variant="default">Help</Button>
                        </Link>
                      </div>
                      <div className="nav-user">
                        <span className="user-name">{user?.full_name}</span>
                        <Button variant="outline" size="sm" onClick={logout}>
                          Logout
                        </Button>
                      </div>
                    </nav>
                    <main className="app-content">
                      <ChangelogPage />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
        <Toaster position="top-right" />
        <EnvironmentBadge />
        <CleanupModeBanner />
      </div>
    </AuthContext.Provider>
  );
}

// Wrap App with BrowserRouter
function AppWithRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public signature route - no auth required */}
        <Route path="/sign/:token" element={<PublicSignaturePage />} />
        {/* Public quote approval route - no auth required */}
        <Route path="/quote-approval/:token" element={<QuoteApprovalPage />} />
        
        {/* All other routes handled by App */}
        <Route path="*" element={<App />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppWithRouter;
;
